import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { User, Class, ClassMember, AssignmentStatusValue, AssignmentCategory, Assignment, RoutineAssignment } from '../types/alarm';
import { ProjectAssignment } from '../types';
import { userDataService } from '../services/userDataService';
import { assignmentService } from '../services/assignmentService';
import { assignmentCategoryService } from '../services/assignmentCategoryService';
import { assignmentTableServiceOptimizedV4, AssignmentTableData, AssignmentInfo } from '../services/assignmentTableServiceOptimizedV4';
import { ProjectAssignmentConsolidatedService } from '../services/projectAssignmentConsolidatedService';
import { routineAssignmentService } from '../services/routineAssignmentService';
import { projectAssignmentService } from '../services/projectAssignmentService';
import CachedProfileImage from '../components/CachedProfileImage';
import AssignmentCheckModal from '../components/AssignmentCheckModal';
import ConfirmModal from '../components/ConfirmModal';
import {
  MemberProfileDisplayMode,
  readMemberProfileDisplayMode,
  resolveMemberProfileSource
} from '../utils/memberProfileDisplay';

// 프로젝트 정보 인터페이스
interface ProjectInfo {
  id: string;
  title: string;
  color: string;
}

interface AssignmentCardPageProps {
  userId: string;
  classId: string;
  onBack: () => void;
  user: User | null;
  currentClass: Class | null;
  currentMemberGroup?: string;
  onHasChangesChange?: (hasChanges: boolean) => void;
  onSaveAndExit?: () => Promise<void>;
  onSaveRef?: (saveFn: () => Promise<void>) => void;
  /** 매니저 과제 관리 모드: 미완료 전체·루틴 과제 탭만 표시, 클래스 멤버 전체 과제 상태 표시 */
  forManager?: boolean;
  /** App 모달 안에 넣을 때: 고정 헤더/여백을 모달 기준으로 맞춤 */
  embeddedInModal?: boolean;
}

// 과제 상태 색상
const statusColors: { [key: string]: { bg: string; text: string } } = {
  pending: { bg: 'var(--status-pending)', text: '#ffffff' },
  confirmed: { bg: 'var(--status-confirmed)', text: '#ffffff' },
  delayed: { bg: 'var(--status-delayed)', text: '#ffffff' },
  exempted: { bg: 'var(--status-exempted)', text: '#1f1703' }
};
const ROTATE_MAX_INDEX = 15;

const AssignmentCardPage: React.FC<AssignmentCardPageProps> = ({
  userId,
  classId,
  onBack,
  user,
  currentClass,
  currentMemberGroup = 'A',
  onHasChangesChange,
  onSaveAndExit,
  onSaveRef,
  forManager = false,
  embeddedInModal = false
}) => {
  // 탭 상태 (기본값: 미완료 전체). 매니저 모드에서는 allIncomplete | routine 만 사용
  const [activeTab, setActiveTab] = useState<'allIncomplete' | 'assignment' | 'project' | 'routine'>('allIncomplete');
  
  // 필터 상태
  const [filterType, setFilterType] = useState<'all' | 'incomplete'>('all');
  const [filterCategoryId, setFilterCategoryId] = useState<string>('all');
  const [filterProjectId, setFilterProjectId] = useState<string>('all');
  const [selectedRoutineDate, setSelectedRoutineDate] = useState<Date>(new Date());
  
  // 드롭다운 상태
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isRoutineDateDropdownOpen, setIsRoutineDateDropdownOpen] = useState(false);
  const routineDateDropdownRef = useRef<HTMLDivElement>(null);
  
  // 데이터 상태
  const [members, setMembers] = useState<ClassMember[]>([]);
  const [memberProfileDisplayMode, setMemberProfileDisplayMode] = useState<MemberProfileDisplayMode>('svg');
  const [tableData, setTableData] = useState<AssignmentTableData | null>(null);
  const [categories, setCategories] = useState<AssignmentCategory[]>([]);
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  
  // 선택된 과제
  const [selectedAssignment, setSelectedAssignment] = useState<AssignmentInfo | null>(null);
  
  // 멤버별 상태 (studentId -> status)
  const [studentStatuses, setStudentStatuses] = useState<Map<string, string>>(new Map());
  
  // 일반 과제 체크 모달 상태
  const [isGeneralAssignmentCheckModalOpen, setIsGeneralAssignmentCheckModalOpen] = useState(false);
  const [selectedGeneralAssignment, setSelectedGeneralAssignment] = useState<Assignment | null>(null);
  const [selectedGeneralAssignmentStatuses, setSelectedGeneralAssignmentStatuses] = useState<any[]>([]);
  
  // 프로젝트 과제 체크 모달 상태
  const [isProjectAssignmentCheckModalOpen, setIsProjectAssignmentCheckModalOpen] = useState(false);
  const [selectedProjectAssignmentForTable, setSelectedProjectAssignmentForTable] = useState<ProjectAssignment | null>(null);
  const [selectedProjectSubTasksForTable, setSelectedProjectSubTasksForTable] = useState<any[]>([]);
  const [selectedSubTaskIdForTable, setSelectedSubTaskIdForTable] = useState<string>('');
  const [selectedProjectAssignmentStatuses, setSelectedProjectAssignmentStatuses] = useState<any[]>([]);
  const [selectedProjectAssignmentIdForTable, setSelectedProjectAssignmentIdForTable] = useState<string>('');
  
  // 루틴 과제 체크 모달 상태
  const [isRoutineAssignmentCheckModalOpen, setIsRoutineAssignmentCheckModalOpen] = useState(false);
  const [selectedRoutineAssignmentForTable, setSelectedRoutineAssignmentForTable] = useState<RoutineAssignment | null>(null);
  const [selectedRoutineDateForTable, setSelectedRoutineDateForTable] = useState<Date>(new Date());
  const [selectedRoutineAssignmentStatuses, setSelectedRoutineAssignmentStatuses] = useState<any[]>([]);
  const [selectedRoutineAssignmentIdForTable, setSelectedRoutineAssignmentIdForTable] = useState<string>('');
  
  // 변경 사항 추적 (과제별로 관리: Map<assignmentId, Map<studentId, status>>)
  const [pendingChangesByAssignment, setPendingChangesByAssignment] = useState<Map<string, Map<string, string>>>(new Map());

  useEffect(() => {
    setMemberProfileDisplayMode(readMemberProfileDisplayMode(userId, classId));
  }, [userId, classId]);

  useEffect(() => {
    const handler = () => {
      setMemberProfileDisplayMode(readMemberProfileDisplayMode(userId, classId));
    };
    window.addEventListener('memberProfileDisplayModeChanged', handler as EventListener);
    return () => {
      window.removeEventListener('memberProfileDisplayModeChanged', handler as EventListener);
    };
  }, [userId, classId]);
  
  // 과제 ID 생성 헬퍼 (assignmentStatuses 키와 동일하게 사용)
  const getAssignmentId = useCallback((assignment: AssignmentInfo | null): string | null => {
    if (!assignment) return null;
    if (assignment.type === 'project' && assignment.subTaskId) return `${assignment.id}-${assignment.subTaskId}`;
    if (assignment.type === 'routine' && assignment.routineAssignmentId && assignment.dateId) return `${assignment.routineAssignmentId}-${assignment.dateId}`;
    return assignment.id;
  }, []);

  // 해당 과제를 부여받은 멤버인지 여부 (과제 체크 모달과 동일: assignedStudentIds 우선 사용)
  const isMemberAssignedToAssignment = useCallback((memberId: string, assignment: AssignmentInfo): boolean => {
    const ids = assignment.assignedStudentIds;
    if (ids && ids.length > 0) return ids.includes(memberId);
    const key = getAssignmentId(assignment);
    if (!key || !tableData?.studentStatuses) return false;
    const entry = tableData.studentStatuses.find(ss => ss.studentId === memberId)?.assignmentStatuses[key];
    return entry?.isAssigned === true;
  }, [tableData?.studentStatuses, getAssignmentId]);

  // 해당 과제에 부여받은 멤버만으로 완료 여부 판별 (assignedStudentIds 우선 사용, 카드·체크 모달과 동일 기준)
  const isAllAssignedCompleted = useCallback((
    assignmentId: string | null,
    studentStatusesList: AssignmentTableData['studentStatuses'] | null | undefined,
    assignment?: AssignmentInfo | null
  ): boolean => {
    if (!assignmentId || !studentStatusesList?.length) return true;
    const ids = assignment?.assignedStudentIds;
    if (ids && ids.length > 0) {
      // 부여받은 멤버만 대상: 각자의 상태가 확인/지연확인/예외인지 검사
      for (const memberId of ids) {
        const ss = studentStatusesList.find(s => s.studentId === memberId);
        const status = ss?.assignmentStatuses[assignmentId]?.status;
        if (status !== 'confirmed' && status !== 'delayed' && status !== 'exempted') return false;
      }
      return true;
    }
    const assignedStatuses = studentStatusesList.filter(ss => ss.assignmentStatuses[assignmentId]?.isAssigned === true);
    if (assignedStatuses.length === 0) return true; // 부여받은 멤버 없음 → 완료로 간주
    return assignedStatuses.every(ss => {
      const status = ss.assignmentStatuses[assignmentId]?.status;
      return status === 'confirmed' || status === 'delayed' || status === 'exempted';
    });
  }, []);
  
  // 현재 선택된 과제의 변경사항 가져오기
  const getCurrentPendingChanges = useCallback((): Map<string, string> => {
    if (!selectedAssignment) return new Map();
    const assignmentId = getAssignmentId(selectedAssignment);
    return assignmentId ? pendingChangesByAssignment.get(assignmentId) || new Map() : new Map();
  }, [selectedAssignment, pendingChangesByAssignment, getAssignmentId]);
  
  // 변경사항이 있는지 확인 (실제로 원래 상태와 다른지 확인)
  const hasChanges = useMemo(() => {
    if (!tableData || pendingChangesByAssignment.size === 0) return false;
    
    // 각 과제의 변경사항을 확인
    for (const [assignmentId, studentChanges] of Array.from(pendingChangesByAssignment.entries())) {
      if (studentChanges.size === 0) continue;
      
      // 각 멤버의 변경사항이 실제로 원래 상태와 다른지 확인
      for (const [studentId, newStatus] of Array.from(studentChanges.entries())) {
        // tableData에서 원래 상태 찾기
        const studentStatus = tableData.studentStatuses.find(ss => ss.studentId === studentId);
        if (!studentStatus) {
          // 원래 상태가 없는데 변경사항이 있으면 변경사항이 있는 것
          return true;
        }
        
        const originalStatus = studentStatus.assignmentStatuses[assignmentId]?.status || 'pending';
        
        // 원래 상태와 다르면 실제 변경사항이 있는 것
        if (originalStatus !== newStatus) {
          return true;
        }
      }
    }
    
    return false;
  }, [pendingChangesByAssignment, tableData]);
  
  // hasChanges 변경 시 부모에게 알림
  useEffect(() => {
    if (onHasChangesChange) {
      onHasChangesChange(hasChanges);
    }
  }, [hasChanges, onHasChangesChange]);
  
  // 저장 함수를 부모에게 전달 (handleSave는 나중에 정의되므로 useRef 사용)
  const handleSaveRef = useRef<(() => Promise<void>) | null>(null);
  
  useEffect(() => {
    if (onSaveRef && handleSaveRef.current) {
      onSaveRef(handleSaveRef.current);
    }
  }, [onSaveRef]);
  
  // 로딩 상태
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // 경고 모달 상태
  const [showWarningModal, setShowWarningModal] = useState(false);
  
  // 그리드 정렬 상태
  const [gridAlignment, setGridAlignment] = useState<'left' | 'center'>(() => {
    const saved = localStorage.getItem('assignmentCardGridAlignment');
    return (saved === 'center' || saved === 'left') ? saved : 'left';
  });
  
  // 선택된 멤버들 (다중 선택)
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [memberViewMode, setMemberViewMode] = useState<'number' | 'seat'>(() => {
    const saved = localStorage.getItem('assignmentCardMemberViewMode');
    return saved === 'seat' ? 'seat' : 'number';
  });
  const [isSeatRotated, setIsSeatRotated] = useState(false);
  
  // gridAlignment 변경 시 localStorage에 저장
  useEffect(() => {
    localStorage.setItem('assignmentCardGridAlignment', gridAlignment);
  }, [gridAlignment]);

  useEffect(() => {
    localStorage.setItem('assignmentCardMemberViewMode', memberViewMode);
  }, [memberViewMode]);
  
  // 선택된 멤버들을 ref로 추적 (useEffect 의존성 문제 해결)
  const selectedStudentsRef = useRef<Set<string>>(new Set());
  selectedStudentsRef.current = selectedStudents;
  
  // 선택된 멤버 일괄 상태 변경 (키보드 1,2,3,4 및 상단 [1][2][3][4] 버튼 공용)
  const handleBatchStatusChange = useCallback((status: string) => {
    if (selectedStudents.size === 0 || !selectedAssignment) return;
    const assignmentId = getAssignmentId(selectedAssignment);
    if (!assignmentId) return;
    setPendingChangesByAssignment(prev => {
      const newMap = new Map(prev);
      const assignmentChanges = newMap.get(assignmentId) || new Map<string, string>();
      const updatedChanges = new Map(assignmentChanges);
      selectedStudents.forEach(studentId => {
        updatedChanges.set(studentId, status);
      });
      newMap.set(assignmentId, updatedChanges);
      return newMap;
    });
  }, [selectedAssignment, selectedStudents, getAssignmentId]);

  // 키보드 이벤트 처리 (1,2,3,4 키로 상태 변경)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedStudentsRef.current.size === 0 || !selectedAssignment) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const statusMap: { [key: string]: string } = {
        '1': 'pending',
        '2': 'confirmed',
        '3': 'delayed',
        '4': 'exempted'
      };
      const status = statusMap[e.key];
      if (status) {
        e.preventDefault();
        const assignmentId = getAssignmentId(selectedAssignment);
        if (!assignmentId) return;
        setPendingChangesByAssignment(prev => {
          const newMap = new Map(prev);
          const assignmentChanges = newMap.get(assignmentId) || new Map<string, string>();
          const updatedChanges = new Map(assignmentChanges);
          selectedStudentsRef.current.forEach(studentId => {
            updatedChanges.set(studentId, status);
          });
          newMap.set(assignmentId, updatedChanges);
          return newMap;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedAssignment, getAssignmentId]);

  // 탭 인덱스 계산 (매니저 모드: 미완료 전체=0, 루틴=1)
  const getTabIndex = () => {
    if (forManager) {
      return activeTab === 'routine' ? 1 : 0;
    }
    switch (activeTab) {
      case 'allIncomplete': return 0;
      case 'assignment': return 1;
      case 'project': return 2;
      case 'routine': return 3;
      default: return 1;
    }
  };

  // 최근 날짜 목록 생성 (루틴용)
  const getRecentDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayOfWeek = date.toLocaleDateString('ko-KR', { weekday: 'short' });
      dates.push({
        date,
        dateId: date.toISOString().split('T')[0],
        dayOfWeek,
        isToday: i === 0
      });
    }
    
    return dates;
  };

  // 프로젝트 제목 기준 색상 (테이블 페이지와 동일)
  const getProjectColor = useCallback((projectTitle: string) => {
    const colors = [
      '#4a90e2', '#7b68ee', '#20b2aa', '#ff6347', '#32cd32',
      '#ffd700', '#ff69b4', '#9370db', '#00ced1', '#ffa500'
    ];
    let hash = 0;
    for (let i = 0; i < projectTitle.length; i++) {
      hash = projectTitle.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }, []);

  // 루틴 과제 색상 가져오기
  const getRoutineAssignmentColor = () => {
    if (!tableData || activeTab !== 'routine') return '#6366f1'; // 기본 루틴 과제 색상
    
    const routineAssignments = tableData.allAssignments.filter(assignment => assignment.type === 'routine');
    if (routineAssignments.length === 0) return '#6366f1'; // 루틴 과제가 없어도 기본 색상 유지
    
    // 첫 번째 루틴 과제의 색상 사용 (모든 루틴 과제가 같은 색상이라고 가정)
    return routineAssignments[0].color || '#6366f1';
  };

  // 데이터 로드 (모달 저장 후 호출 시 선택 복원용으로 restoreSelectionId 전달)
  const loadData = useCallback(async (restoreSelectionId?: string) => {
    if (!userId || !classId) return;
    
    setIsLoading(true);
    try {
      // 멤버 목록 로드
      const loadedMembers = await userDataService.loadClassMembers(userId, classId, currentMemberGroup);
      setMembers(loadedMembers.sort((a, b) => {
        const numA = parseInt(a.number) || 0;
        const numB = parseInt(b.number) || 0;
        return numA - numB;
      }));
      
      // 미완료 전체 탭인 경우 3가지 타입 모두 로드
      if (activeTab === 'allIncomplete') {
        // 3가지 타입의 데이터를 병렬로 로드
        const [generalData, projectData, routineData] = await Promise.all([
          assignmentTableServiceOptimizedV4.loadAssignmentTableData(userId, classId, 'general', new Date(), currentMemberGroup),
          assignmentTableServiceOptimizedV4.loadAssignmentTableData(userId, classId, 'project', new Date(), currentMemberGroup),
          assignmentTableServiceOptimizedV4.loadAssignmentTableData(userId, classId, 'routine', new Date(), currentMemberGroup) // 오늘 날짜
        ]);
        
        // 미완료 과제만 필터링 (부여받은 멤버만 카운트, assignedStudentIds 우선)
        // 프로젝트: 테이블의 assignment.id가 이미 "projectId-subTaskId" 복합키이므로 a.id만 사용
        const getKey = (a: AssignmentInfo) => a.type === 'project' ? a.id : a.type === 'routine' && a.routineAssignmentId && a.dateId ? `${a.routineAssignmentId}-${a.dateId}` : a.id;
        const filterIncomplete = (assignments: typeof generalData.allAssignments, studentStatuses: typeof generalData.studentStatuses) => {
          return assignments.filter(assignment => {
            const assignmentId = getKey(assignment);
            const ids = assignment.assignedStudentIds;
            let isAllCompleted: boolean;
            if (ids && ids.length > 0) {
              isAllCompleted = ids.every(memberId => {
                const ss = studentStatuses?.find(s => s.studentId === memberId);
                const status = ss?.assignmentStatuses[assignmentId]?.status;
                return status === 'confirmed' || status === 'delayed' || status === 'exempted';
              });
            } else {
              const assigned = studentStatuses?.filter(ss => ss.assignmentStatuses[assignmentId]?.isAssigned === true) ?? [];
              isAllCompleted = assigned.length === 0 || assigned.every(ss => {
                const status = ss.assignmentStatuses[assignmentId]?.status;
                return status === 'confirmed' || status === 'delayed' || status === 'exempted';
              });
            }
            return !isAllCompleted;
          });
        };
        
        // 각 타입에서 미완료 과제만 추출
        const incompleteGeneral = filterIncomplete(generalData.allAssignments || [], generalData.studentStatuses || []);
        const incompleteProject = filterIncomplete(projectData.allAssignments || [], projectData.studentStatuses || []);
        const incompleteRoutine = filterIncomplete(routineData.allAssignments || [], routineData.studentStatuses || []);
        
        // 모든 미완료 과제를 합침
        const allIncompleteAssignments = [...incompleteGeneral, ...incompleteProject, ...incompleteRoutine];
        
        // studentStatuses도 합침
        const combinedStudentStatuses = [...(generalData.studentStatuses || [])];
        projectData.studentStatuses?.forEach(ps => {
          const existing = combinedStudentStatuses.find(s => s.studentId === ps.studentId);
          if (existing) {
            existing.assignmentStatuses = { ...existing.assignmentStatuses, ...ps.assignmentStatuses };
          } else {
            combinedStudentStatuses.push(ps);
          }
        });
        routineData.studentStatuses?.forEach(ps => {
          const existing = combinedStudentStatuses.find(s => s.studentId === ps.studentId);
          if (existing) {
            existing.assignmentStatuses = { ...existing.assignmentStatuses, ...ps.assignmentStatuses };
          } else {
            combinedStudentStatuses.push(ps);
          }
        });
        
        const combinedData = {
          ...generalData,
          allAssignments: allIncompleteAssignments,
          studentStatuses: combinedStudentStatuses
        };
        
        setTableData(combinedData);
        
        // 카테고리 로드 (일반 과제 색상 표시용)
        const loadedCategories = await assignmentCategoryService.loadCategories(userId, classId);
        setCategories(loadedCategories);
        
        // 선택 복원 또는 첫 번째 과제 자동 선택
        if (allIncompleteAssignments.length > 0) {
          const toSelect = restoreSelectionId
            ? allIncompleteAssignments.find(a => getKey(a) === restoreSelectionId) ?? allIncompleteAssignments[0]
            : allIncompleteAssignments[0];
          setSelectedAssignment(toSelect);
          loadStudentStatusesForAssignment(toSelect, combinedData, loadedMembers);
        } else {
          setSelectedAssignment(null);
          setStudentStatuses(new Map());
        }
      } else {
        // 현재 탭에 따라 과제 데이터 로드
        const assignmentType = activeTab === 'assignment' ? 'general' : 
                               activeTab === 'project' ? 'project' : 'routine';
        
        const data = await assignmentTableServiceOptimizedV4.loadAssignmentTableData(
          userId, 
          classId, 
          assignmentType,
          activeTab === 'routine' ? selectedRoutineDate : new Date(),
          currentMemberGroup
        );
        
        setTableData(data);
        
        // 카테고리 로드 (일반 과제 탭)
        if (activeTab === 'assignment') {
          const loadedCategories = await assignmentCategoryService.loadCategories(userId, classId);
          setCategories(loadedCategories);
        }
        
        // 선택 복원 또는 첫 번째 과제 자동 선택
        if (data.allAssignments.length > 0) {
          // 프로젝트: 테이블의 assignment.id가 이미 "projectId-subTaskId" 복합키이므로 a.id만 사용
          const getKeyForSelect = (a: AssignmentInfo) => a.type === 'project' ? a.id : a.type === 'routine' && a.routineAssignmentId && a.dateId ? `${a.routineAssignmentId}-${a.dateId}` : a.id;
          const toSelect = restoreSelectionId
            ? data.allAssignments.find(a => getKeyForSelect(a) === restoreSelectionId) ?? data.allAssignments[0]
            : data.allAssignments[0];
          setSelectedAssignment(toSelect);
          loadStudentStatusesForAssignment(toSelect, data, loadedMembers);
        } else {
          setSelectedAssignment(null);
          setStudentStatuses(new Map());
        }
      }
      
    } catch (error) {
      console.error('데이터 로드 실패:', error);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, classId, activeTab, selectedRoutineDate, currentMemberGroup]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 테이블 페이지와 동일: tableData에서 프로젝트 종류 추출 (projectTitle 기준, slug id)
  useEffect(() => {
    if (tableData?.allAssignments) {
      const projectMap = new Map<string, ProjectInfo>();
      tableData.allAssignments.forEach(assignment => {
        if (assignment.type === 'project' && assignment.projectTitle) {
          const projectId = assignment.projectTitle.replace(/\s+/g, '-').toLowerCase();
          if (!projectMap.has(projectId)) {
            projectMap.set(projectId, {
              id: projectId,
              title: assignment.projectTitle,
              color: (assignment as any).projectColor || (assignment as any).color || getProjectColor(assignment.projectTitle)
            });
          }
        }
      });
      setProjects(Array.from(projectMap.values()));
    } else {
      setProjects([]);
    }
  }, [tableData, getProjectColor]);

  // 과제 선택 시 멤버 상태 로드
  const loadStudentStatusesForAssignment = useCallback((assignment: AssignmentInfo, data: AssignmentTableData, membersList?: ClassMember[]) => {
    if (!assignment || !data) return;
    
    const newStatusMap = new Map<string, string>();
    
    // 과제 ID 계산
    const assignmentId = getAssignmentId(assignment);
    if (!assignmentId) return;
    
    // membersList가 제공되면 사용하고, 없으면 현재 members 상태 사용
    const membersToUse = membersList || members;
    
    // tableData에서 해당 과제의 멤버 상태 찾기
    membersToUse.forEach(member => {
      const studentStatus = data.studentStatuses.find(
        ss => ss.studentId === member.id
      );
      
      if (studentStatus && studentStatus.assignmentStatuses[assignmentId]) {
        newStatusMap.set(member.id, studentStatus.assignmentStatuses[assignmentId].status || 'pending');
      } else {
        newStatusMap.set(member.id, 'pending');
      }
    });
    
    setStudentStatuses(newStatusMap);
    // pendingChanges는 복원하지 않음 (getDisplayStatus에서 pendingChangesByAssignment를 참조)
  }, [members, getAssignmentId]);

  // 과제 선택 핸들러 (이미 선택된 과제를 다시 클릭해도 선택 해제하지 않음)
  const handleSelectAssignment = (assignment: AssignmentInfo) => {
    // 현재 선택된 과제와 동일한지 확인
    const isSameAssignment = selectedAssignment && (
      (assignment.type === 'project' && assignment.subTaskId)
        ? `${assignment.id}-${assignment.subTaskId}` === `${selectedAssignment.id}-${selectedAssignment.subTaskId}`
        : assignment.id === selectedAssignment.id
    );
    
    // 이미 선택된 과제를 다시 클릭해도 선택 해제하지 않음
    if (!isSameAssignment) {
      // 새로운 과제 선택
      setSelectedAssignment(assignment);
      if (tableData) {
        loadStudentStatusesForAssignment(assignment, tableData);
      }
    }
  };

  // 일반 과제 더블클릭 핸들러
  const handleGeneralAssignmentDoubleClick = async (assignmentId: string) => {
    console.log('🔍 일반 과제 더블클릭:', { assignmentId });
    
    if (!tableData || !user?.id || !currentClass?.id) return;

    try {
      const assignment = tableData.allAssignments.find(a => a.id === assignmentId && a.type === 'assignment');
      if (!assignment) {
        console.error('❌ 과제를 찾을 수 없습니다:', assignmentId);
        return;
      }

      const assignmentData = await assignmentService.loadAssignmentWithStatuses(
        user.id,
        currentClass.id,
        assignmentId
      );

      setSelectedGeneralAssignment(assignmentData.assignment);
      setSelectedGeneralAssignmentStatuses(assignmentData.statuses || []);
      setIsGeneralAssignmentCheckModalOpen(true);

    } catch (error) {
      console.error('❌ 일반 과제 문서 로드 실패:', error);
      alert('과제 정보를 불러오는 중 오류가 발생했습니다.');
    }
  };

  // 프로젝트 과제 더블클릭 핸들러
  const handleProjectAssignmentDoubleClick = async (assignmentId: string, subTaskId: string) => {
    console.log('🔍 프로젝트 과제 더블클릭:', { assignmentId, subTaskId });
    
    if (!tableData || !user?.id || !currentClass?.id) return;

    try {
      const assignment = tableData.allAssignments.find(a => 
        a.id === assignmentId && a.type === 'project' && a.subTaskId === subTaskId
      );
      if (!assignment) {
        console.error('❌ 프로젝트 과제를 찾을 수 없습니다:', { assignmentId, subTaskId });
        alert('프로젝트 과제를 찾을 수 없습니다.');
        return;
      }

      const projectId = assignmentId.split('-')[0];
      const projectData = await projectAssignmentService.loadProjectAssignmentWithStatuses(
        user.id,
        currentClass.id,
        projectId
      );

      if (!projectData.projectAssignment) {
        alert('프로젝트 과제 정보가 비어있습니다.');
        return;
      }

      const studentStatuses = await projectAssignmentService.loadProjectAssignmentStudentStatuses(
        user.id,
        currentClass.id,
        projectId,
        subTaskId
      );

      const targetSubTask = projectData.projectAssignment.subTasks?.find((st: any) => st.id === subTaskId);
      if (!targetSubTask) {
        alert('하위 과제를 찾을 수 없습니다.');
        return;
      }

      setSelectedProjectAssignmentForTable(projectData.projectAssignment);
      setSelectedProjectSubTasksForTable([{ subTask: targetSubTask }]);
      setSelectedSubTaskIdForTable(subTaskId);
      setSelectedProjectAssignmentStatuses(studentStatuses);
      setSelectedProjectAssignmentIdForTable(assignmentId);
      setIsProjectAssignmentCheckModalOpen(true);

    } catch (error) {
      console.error('❌ 프로젝트 과제 문서 로드 실패:', error);
      alert(`프로젝트 과제 정보를 불러오는 중 오류가 발생했습니다: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // 루틴 과제 더블클릭 핸들러
  const handleRoutineAssignmentDoubleClick = async (assignmentId: string) => {
    console.log('🔍 루틴 과제 더블클릭:', { assignmentId });
    
    if (!tableData || !user?.id || !currentClass?.id) return;

    try {
      const assignment = tableData.allAssignments.find(a => a.id === assignmentId && a.type === 'routine');
      if (!assignment) {
        console.error('❌ 루틴 과제를 찾을 수 없습니다:', assignmentId);
        return;
      }

      const routineId = assignment.routineAssignmentId || assignmentId.split('-').slice(0, -1).join('-');
      const dateMatch = assignmentId.match(/(\d{4}-\d{2}-\d{2})$/);
      let clickedDate: Date;
      if (dateMatch) {
        clickedDate = new Date(dateMatch[1] + 'T00:00:00');
      } else {
        clickedDate = new Date();
        clickedDate.setHours(0, 0, 0, 0);
      }

      const routineData = await routineAssignmentService.loadRoutineAssignmentWithStatuses(
        user.id,
        currentClass.id,
        routineId,
        currentMemberGroup
      );

      if (!routineData.routine) {
        alert('루틴 과제 정보를 찾을 수 없습니다.');
        return;
      }

      const dateId = clickedDate.toISOString().split('T')[0];
      const dateStatus = routineData.dateStatuses?.find(d => d.dateId === dateId);
      const studentStatuses = dateStatus?.students ? Object.entries(dateStatus.students).map(([studentId, status]: [string, any]) => ({
        studentId,
        status: status.status || 'pending',
        earnedPoints: status.earnedPoints || 0,
        memo: status.memo || '',
        notes: status.notes || []
      })) : [];

      setSelectedRoutineAssignmentForTable(routineData.routine);
      setSelectedRoutineDateForTable(clickedDate);
      setSelectedRoutineAssignmentStatuses(studentStatuses);
      setSelectedRoutineAssignmentIdForTable(assignmentId);
      setIsRoutineAssignmentCheckModalOpen(true);

    } catch (error) {
      console.error('❌ 루틴 과제 문서 로드 실패:', error);
      alert('루틴 과제 정보를 불러오는 중 오류가 발생했습니다.');
    }
  };

  // 상태별 포인트 계산 함수
  const calculatePoints = useCallback(async (status: string, assignment: AssignmentInfo, userId: string, classId: string): Promise<number> => {
    try {
      // 1. 미확인: 포인트 0
      if (status === 'pending') {
        return 0;
      }
      
      // 2. 확인: 해당 과제문서에서 설정한 포인트
      if (status === 'confirmed') {
        try {
          if (assignment.type === 'assignment') {
            // 일반 과제의 경우 실제 과제 객체에서 포인트 가져오기
            const result = await assignmentService.loadAssignmentWithStatuses(userId, classId, assignment.id);
            return result.assignment?.basePoints || 0;
          } else if (assignment.type === 'project') {
            // 프로젝트 과제의 경우 하위 과제의 포인트를 가져와야 함
            const projectId = assignment.id.split('-')[0]; // project_1758462184304
            const subTaskId = assignment.subTaskId;
            if (subTaskId) {
              const projectAssignment = await projectAssignmentService.getProjectAssignment(userId, classId, projectId, currentMemberGroup);
              const subTask = projectAssignment?.subTasks?.find(st => st.id === subTaskId);
              return subTask?.points || 0; // SubTask에는 basePoints가 아닌 points 사용
            }
            return 0;
          } else if (assignment.type === 'routine') {
            // 루틴 과제의 경우 루틴 과제의 포인트를 가져와야 함
            const routineId = assignment.routineAssignmentId || assignment.id;
            const result = await routineAssignmentService.loadRoutineAssignmentWithStatuses(userId, classId, routineId, currentMemberGroup);
            return result.routine?.basePoints || 0;
          }
        } catch (error) {
          console.error('❌ 과제 정보 로드 실패:', error);
          return 0;
        }
        return 0;
      }
      
      // 3. 지연확인, 예외: 확인 포인트의 설정된 백분율만큼 지급 (반올림)
      if (status === 'delayed' || status === 'exempted') {
        let confirmedPoints = 0;
        try {
          if (assignment.type === 'assignment') {
            const result = await assignmentService.loadAssignmentWithStatuses(userId, classId, assignment.id);
            confirmedPoints = result.assignment?.basePoints || 0;
          } else if (assignment.type === 'project') {
            const projectId = assignment.id.split('-')[0];
            const subTaskId = assignment.subTaskId;
            if (subTaskId) {
              const projectAssignment = await projectAssignmentService.getProjectAssignment(userId, classId, projectId, currentMemberGroup);
              const subTask = projectAssignment?.subTasks?.find(st => st.id === subTaskId);
              confirmedPoints = subTask?.points || 0;
            }
          } else if (assignment.type === 'routine') {
            const routineId = assignment.routineAssignmentId || assignment.id;
            const result = await routineAssignmentService.loadRoutineAssignmentWithStatuses(userId, classId, routineId, currentMemberGroup);
            confirmedPoints = result.routine?.basePoints || 0;
          }
        } catch {
          confirmedPoints = 0;
        }
        const settings = await userDataService.loadAssignmentSettings(userId, classId);
        const percent = settings?.statusDefaultPoints?.[status] ?? 0;
        return Math.round(confirmedPoints * (percent || 0) / 100);
      }
      
      return 0;
    } catch (error) {
      console.error('❌ 포인트 계산 실패:', error);
      return 0;
    }
  }, [userId, classId, currentMemberGroup]);

  // 상태 변경 핸들러 (원래 상태로 되돌리면 pendingChanges에서 제거)
  const handleStatusChange = useCallback((studentId: string, newStatus: string) => {
    if (!selectedAssignment) return;
    
    const assignmentId = getAssignmentId(selectedAssignment);
    if (!assignmentId) return;
    
    const originalStatus = studentStatuses.get(studentId) || 'pending';
    
    setPendingChangesByAssignment(prev => {
      const newMap = new Map(prev);
      const assignmentChanges = newMap.get(assignmentId) || new Map<string, string>();
      const updatedChanges = new Map(assignmentChanges);
      
      if (newStatus === originalStatus) {
        // 원래 상태로 되돌리면 변경사항 제거
        updatedChanges.delete(studentId);
      } else {
        updatedChanges.set(studentId, newStatus);
      }
      
      if (updatedChanges.size === 0) {
        newMap.delete(assignmentId);
      } else {
        newMap.set(assignmentId, updatedChanges);
      }
      return newMap;
    });
  }, [selectedAssignment, getAssignmentId, studentStatuses]);
  
  // 멤버 선택 토글 (다중 선택) — 과제를 부여받지 않은 멤버는 선택 불가
  const handleStudentSelect = useCallback((studentId: string) => {
    if (selectedAssignment && !isMemberAssignedToAssignment(studentId, selectedAssignment)) return;
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  }, [selectedAssignment, isMemberAssignedToAssignment]);
  
  // 전체 선택 — 현재 과제를 부여받은 멤버만 선택
  const handleSelectAll = useCallback(() => {
    if (!selectedAssignment) {
      setSelectedStudents(new Set(members.map(m => m.id)));
      return;
    }
    const assignedIds = members.filter(m => isMemberAssignedToAssignment(m.id, selectedAssignment)).map(m => m.id);
    setSelectedStudents(new Set(assignedIds));
  }, [members, selectedAssignment, isMemberAssignedToAssignment]);
  
  // 선택 취소
  const handleDeselectAll = useCallback(() => {
    setSelectedStudents(new Set());
  }, []);

  // 현재 표시할 상태 가져오기
  const getDisplayStatus = (studentId: string): string => {
    const currentPendingChanges = getCurrentPendingChanges();
    return currentPendingChanges.get(studentId) || studentStatuses.get(studentId) || 'pending';
  };

  // 자리 모드 데이터 (최근 자리배치 월 기준)
  const seatModeData = useMemo(() => {
    const monthKeys = new Set<string>();
    members.forEach((member) => {
      if (member.seatPositions && typeof member.seatPositions === 'object') {
        Object.keys(member.seatPositions).forEach((month) => monthKeys.add(month));
      }
    });

    const sortedMonths = Array.from(monthKeys).sort();
    const selectedMonth = sortedMonths[sortedMonths.length - 1] || `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

    const assigned: Array<{ member: ClassMember; seatRow: number; seatCol: number }> = [];
    const unassigned: ClassMember[] = [];

    members.forEach((member) => {
      const seatPos = member.seatPositions?.[selectedMonth];
      if (
        seatPos &&
        typeof seatPos.row === 'number' &&
        typeof seatPos.col === 'number'
      ) {
        assigned.push({ member, seatRow: seatPos.row, seatCol: seatPos.col });
      } else {
        unassigned.push(member);
      }
    });

    const rotatedAssigned = assigned.map((item) => ({
      member: item.member,
      seatRow: isSeatRotated ? ROTATE_MAX_INDEX - item.seatRow : item.seatRow,
      seatCol: isSeatRotated ? ROTATE_MAX_INDEX - item.seatCol : item.seatCol
    }));

    let minRow = 0;
    let maxRow = ROTATE_MAX_INDEX;
    let minCol = 0;
    let maxCol = ROTATE_MAX_INDEX;
    if (rotatedAssigned.length > 0) {
      const rows = rotatedAssigned.map((a) => a.seatRow);
      const cols = rotatedAssigned.map((a) => a.seatCol);
      minRow = Math.max(0, Math.min(...rows));
      maxRow = Math.min(ROTATE_MAX_INDEX, Math.max(...rows));
      minCol = Math.max(0, Math.min(...cols));
      maxCol = Math.min(ROTATE_MAX_INDEX, Math.max(...cols));
    }

    const rowCount = Math.max(2, maxRow - minRow + 2);
    const colCount = Math.max(2, maxCol - minCol + 2);
    const usedGridRows = new Set<number>();
    const usedGridCols = new Set<number>();
    rotatedAssigned.forEach(({ seatRow, seatCol }) => {
      const rowIndex = seatRow - minRow;
      const colIndex = seatCol - minCol;
      [rowIndex, rowIndex + 1].forEach((idx) => {
        if (idx >= 0 && idx < rowCount) usedGridRows.add(idx);
      });
      [colIndex, colIndex + 1].forEach((idx) => {
        if (idx >= 0 && idx < colCount) usedGridCols.add(idx);
      });
    });

    const rowTemplate = Array.from(
      { length: rowCount },
      (_, idx) => (usedGridRows.has(idx) ? '43px' : '21px')
    ).join(' ');
    const colTemplate = Array.from(
      { length: colCount },
      (_, idx) => (usedGridCols.has(idx) ? '92px' : '46px')
    ).join(' ');

    return {
      selectedMonth,
      assigned: rotatedAssigned,
      unassigned,
      minRow,
      minCol,
      rowCount,
      colCount,
      rowTemplate,
      colTemplate
    };
  }, [members, isSeatRotated]);

  // 뒤로가기 핸들러 (변경사항 확인)
  const handleBack = () => {
    if (hasChanges) {
      setShowWarningModal(true);
    } else {
      onBack();
    }
  };
  
  // 저장하고 이동
  const handleSaveAndExitLocal = async () => {
    await handleSave();
    setShowWarningModal(false);
    // 저장 후 항상 onBack 호출 (뒤로가기 버튼의 경우)
    onBack();
  };
  
  // 저장하지 않고 닫기(뒤로)
  const handleExitWithoutSave = () => {
    setShowWarningModal(false);
    onBack();
  };

  // 경고 모달 바깥 클릭: 경고만 닫고 카드/테이블 모달 유지
  const handleCloseWarningOnly = () => {
    setShowWarningModal(false);
  };
  
  // 저장 핸들러 (모든 과제의 변경사항을 한 번에 저장)
  const handleSave = useCallback(async () => {
    if (!userId || !classId || !tableData || pendingChangesByAssignment.size === 0) return;
    
    setIsSaving(true);
    try {
      // 모든 과제의 변경사항을 순회하며 저장
      for (const [assignmentId, studentChanges] of Array.from(pendingChangesByAssignment.entries())) {
        if (studentChanges.size === 0) continue;
        
        // assignmentId에서 실제 과제 정보 찾기 (assignmentStatuses 키와 동일)
        const assignment = tableData.allAssignments.find(a => getAssignmentId(a) === assignmentId);
        
        if (!assignment) continue;
        
        // 멤버별 업데이트 데이터 생성 (상태별 포인트 계산)
        const updates = await Promise.all(
          Array.from(studentChanges.entries()).map(async ([studentId, status]) => {
            const earnedPoints = await calculatePoints(status, assignment, userId, classId);
            return {
              studentId,
              status: status as AssignmentStatusValue,
              earnedPoints,
              memo: ''
            };
          })
        );
        
        // 과제 타입에 따라 저장
        if (assignment.type === 'assignment') {
          // 일반 과제
          await assignmentService.batchUpdateStudentStatusesOptimized(
            userId,
            classId,
            assignment.id,
            updates
          );
        } else if (assignment.type === 'project' && assignment.subTaskId) {
          // 프로젝트 과제: assignment.id는 "projectId-subTaskId" 복합키이므로, consolidated 문서 키인 projectId만 전달
          const projectId = assignment.id.endsWith(`-${assignment.subTaskId}`)
            ? assignment.id.slice(0, -(assignment.subTaskId.length + 1))
            : assignment.id.split('-')[0];
          const consolidatedService = new ProjectAssignmentConsolidatedService(userId, classId, currentMemberGroup);
          await consolidatedService.batchUpdateSubTaskStudentStatuses(
            projectId,
            assignment.subTaskId,
            updates.map(update => ({
              studentId: update.studentId,
              status: update.status,
              points: update.earnedPoints || 0,
              memo: update.memo || ''
            }))
          );
        } else if (assignment.type === 'routine' && assignment.routineAssignmentId && assignment.dateId) {
          // 루틴 과제
          for (const update of updates) {
            await routineAssignmentService.updateRoutineAssignmentStatus(
              userId,
              classId,
              assignment.routineAssignmentId,
              assignment.dateId,
              update.studentId,
              {
                status: update.status,
                earnedPoints: update.earnedPoints,
                memo: update.memo
              },
              currentMemberGroup
            );
          }
        }
      }
      
      // tableData 업데이트 (모든 변경사항 반영)
      if (tableData) {
        const newStudentStatuses = tableData.studentStatuses.map(studentStatus => {
          let updated = false;
          const newAssignmentStatuses = { ...studentStatus.assignmentStatuses };
          
          // 모든 과제의 변경사항을 반영
          for (const [assignmentId, studentChanges] of Array.from(pendingChangesByAssignment.entries())) {
            const newStatus = studentChanges.get(studentStatus.studentId);
            if (newStatus && newAssignmentStatuses[assignmentId]) {
              const existingAssignment = newAssignmentStatuses[assignmentId];
              newAssignmentStatuses[assignmentId] = {
                ...existingAssignment,
                status: newStatus as typeof existingAssignment.status
              };
              updated = true;
            }
          }
          
          return updated ? {
            ...studentStatus,
            assignmentStatuses: newAssignmentStatuses
          } : studentStatus;
        });
        
        setTableData({ ...tableData, studentStatuses: newStudentStatuses });
      }
      
      // 현재 선택된 과제의 studentStatuses도 업데이트
      if (selectedAssignment) {
        const assignmentId = getAssignmentId(selectedAssignment);
        if (assignmentId) {
          const currentChanges = pendingChangesByAssignment.get(assignmentId);
          if (currentChanges) {
            setStudentStatuses(prev => {
              const newMap = new Map(prev);
              currentChanges.forEach((status, studentId) => {
                newMap.set(studentId, status);
              });
              return newMap;
            });
          }
        }
      }
      
      // 모든 변경사항 초기화
      setPendingChangesByAssignment(new Map());
      
      console.log('✅ 모든 과제 변경사항 저장 완료');
    } catch (error) {
      console.error('저장 실패:', error);
    } finally {
      setIsSaving(false);
    }
  }, [userId, classId, tableData, pendingChangesByAssignment, selectedAssignment, getAssignmentId, calculatePoints]);
  
  // handleSave가 변경될 때마다 ref 업데이트
  useEffect(() => {
    handleSaveRef.current = handleSave;
    if (onSaveRef) {
      onSaveRef(handleSave);
    }
  }, [handleSave, onSaveRef]);

  // 필터링된 과제 목록 (마감일 기준: 가장 미래가 왼쪽, 과거가 오른쪽)
  const filteredAssignments = (tableData?.allAssignments || [])
    .filter(assignment => {
      // 카테고리 필터 (일반 과제)
      if (activeTab === 'assignment' && filterCategoryId !== 'all') {
        if (assignment.categoryId !== filterCategoryId) return false;
      }
      
      // 프로젝트 필터 (테이블 페이지와 동일: projectTitle 기준)
      if (activeTab === 'project' && filterProjectId !== 'all') {
        const selectedProject = projects.find(p => p.id === filterProjectId);
        if (!selectedProject || assignment.projectTitle !== selectedProject.title) return false;
      }
      
      // 미완료 필터 - 부여받은 멤버만 카운트하여 완료 여부 판별 (assignedStudentIds 기준)
      if (filterType === 'incomplete') {
        const assignmentId = getAssignmentId(assignment);
        const isAllCompleted = isAllAssignedCompleted(assignmentId, tableData?.studentStatuses ?? [], assignment);
        return !isAllCompleted;
      }
      return true;
    })
    .sort((a, b) => {
      // 미완료 전체 탭: 1단계 일반→프로젝트→루틴, 2단계 각 타입 내 생성일/하위순서 기준
      const typeOrder = (t: AssignmentInfo['type']) => (t === 'assignment' ? 0 : t === 'project' ? 1 : 2);
      if (activeTab === 'allIncomplete') {
        const orderA = typeOrder(a.type);
        const orderB = typeOrder(b.type);
        if (orderA !== orderB) return orderA - orderB;
      }
      // 프로젝트 과제: 프로젝트끼리는 생성일 최신→과거, 같으면 프로젝트 ID로 고정 (저장 후 순서 유지)
      if (a.type === 'project' && b.type === 'project') {
        const projectIdA = a.id.replace(/-[^-]+$/, '');
        const projectIdB = b.id.replace(/-[^-]+$/, '');
        if (projectIdA !== projectIdB) {
          const timeA = a.projectCreatedAt?.getTime() ?? 0;
          const timeB = b.projectCreatedAt?.getTime() ?? 0;
          const byTime = timeB - timeA; // 최신 생성 프로젝트가 왼쪽
          if (byTime !== 0) return byTime;
          return projectIdA.localeCompare(projectIdB); // 동일 생성일이면 ID로 고정 순서
        }
        return (b.subTaskOrder ?? 0) - (a.subTaskOrder ?? 0); // 마지막 하위과제가 왼쪽
      }
      // 일반/루틴: 최초 생성일 기준, 최신이 왼쪽
      const timeA = a.createdAt?.getTime() ?? (a.dueDate ?? a.projectDueDate) != null ? new Date((a.dueDate ?? a.projectDueDate) as Date).getTime() : 0;
      const timeB = b.createdAt?.getTime() ?? (b.dueDate ?? b.projectDueDate) != null ? new Date((b.dueDate ?? b.projectDueDate) as Date).getTime() : 0;
      return timeB - timeA;
    });

  // 선택된 과제가 현재 필터된 과제 목록에 있는지 확인하고, 없으면 선택 해제
  useEffect(() => {
    if (selectedAssignment) {
      const assignmentExists = filteredAssignments.some(a => {
        const aId = a.type === 'project' && a.subTaskId 
          ? `${a.id}-${a.subTaskId}` 
          : a.id;
        const selectedId = selectedAssignment.type === 'project' && selectedAssignment.subTaskId 
          ? `${selectedAssignment.id}-${selectedAssignment.subTaskId}` 
          : selectedAssignment.id;
        return aId === selectedId;
      });
      if (!assignmentExists) {
        setSelectedAssignment(null);
      }
    }
  }, [filteredAssignments, selectedAssignment]);


  if (isLoading) {
    return (
      <Container $embeddedInModal={embeddedInModal}>
        <Header $embeddedInModal={embeddedInModal}>
        {!embeddedInModal && (
        <BackButton onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </BackButton>
        )}
        <Title $embeddedInModal={embeddedInModal}>{forManager ? '매니저 과제 관리 카드' : '과제 관리 카드'}</Title>
        {embeddedInModal ? (
          <ModalCloseButton onClick={onBack} aria-label="모달 닫기">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </ModalCloseButton>
        ) : (
        <div style={{ width: '28px' }} />
        )}
      </Header>
        <Content $embeddedInModal={embeddedInModal}>
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>데이터를 불러오는 중...</LoadingText>
          </LoadingContainer>
        </Content>
      </Container>
    );
  }

  return (
    <Container $embeddedInModal={embeddedInModal}>
      <Header $embeddedInModal={embeddedInModal}>
        {!embeddedInModal && (
        <BackButton onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </BackButton>
        )}
        <Title $embeddedInModal={embeddedInModal}>{forManager ? '매니저 과제 관리 카드' : '과제 관리 카드'}</Title>
        {embeddedInModal ? (
          <ModalCloseButton onClick={onBack} aria-label="모달 닫기">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </ModalCloseButton>
        ) : (
        <div style={{ width: '28px' }} />
        )}
      </Header>

      <Content $embeddedInModal={embeddedInModal}>
        <TopSection>
          {/* 탭 토글 (매니저 모드: 미완료 전체, 루틴 과제만) */}
          <ToggleSection>
            <ToggleContainer $tabCount={forManager ? 2 : 4}>
              <ToggleSlider $position={getTabIndex()} $tabCount={forManager ? 2 : 4} />
              <ToggleOption 
                $active={activeTab === 'allIncomplete'} 
                onClick={() => setActiveTab('allIncomplete')}
              >
                <ToggleText>미완료 전체</ToggleText>
                <ToggleTextShort>미완료</ToggleTextShort>
              </ToggleOption>
              {!forManager && (
                <>
                  <ToggleOption 
                    $active={activeTab === 'assignment'} 
                    onClick={() => {
                      setActiveTab('assignment');
                      setFilterCategoryId('all');
                    }}
                  >
                    <ToggleText>일반 과제</ToggleText>
                    <ToggleTextShort>일반</ToggleTextShort>
                  </ToggleOption>
                  <ToggleOption 
                    $active={activeTab === 'project'} 
                    onClick={() => {
                      setActiveTab('project');
                      setFilterProjectId('all');
                    }}
                  >
                    <ToggleText>프로젝트 과제</ToggleText>
                    <ToggleTextShort>프로젝트</ToggleTextShort>
                  </ToggleOption>
                </>
              )}
              <ToggleOption 
                $active={activeTab === 'routine'} 
                onClick={() => setActiveTab('routine')}
              >
                <ToggleText>루틴 과제</ToggleText>
                <ToggleTextShort>루틴</ToggleTextShort>
              </ToggleOption>
            </ToggleContainer>
          </ToggleSection>

          {/* 필터 영역 (매니저 모드: 루틴 탭일 때만 날짜 드롭다운 + 저장 버튼) */}
          <FilterSection>
            <ToggleButtonSection>
              {/* 일반 과제: 카테고리 드롭다운 (매니저 모드에서는 미표시) */}
              {!forManager && activeTab === 'assignment' && (
                <FilterSelectContainer>
                  <FilterSelectButton 
                    onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                  >
                    <FilterDropdownTrigger>
                      <ColorDot color={filterCategoryId === 'all' ? 'var(--color-border)' : 
                        categories.find(cat => cat.id === filterCategoryId)?.color || 'var(--color-border)'} />
                      <span>
                        {filterType === 'all' ? '전체 과제' : filterType === 'incomplete' ? '미완료 과제' : ''}
                        {filterCategoryId === 'all' ? (filterType === 'all' || filterType === 'incomplete' ? ' · 모든 카테고리' : '모든 카테고리') : 
                         ' · ' + (categories.find(cat => cat.id === filterCategoryId)?.name || '카테고리 선택')}
                      </span>
                    </FilterDropdownTrigger>
                  </FilterSelectButton>
                  
                  {isCategoryDropdownOpen && (
                    <>
                      <FilterDropdownOverlay onClick={() => setIsCategoryDropdownOpen(false)} />
                      <FilterDropdown $isOpen={isCategoryDropdownOpen}>
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterType('all');
                            setIsCategoryDropdownOpen(false);
                          }}
                        >
                          <span>전체 과제</span>
                        </FilterOption>
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterType('incomplete');
                            setIsCategoryDropdownOpen(false);
                          }}
                        >
                          <span>미완료 과제</span>
                        </FilterOption>
                        <FilterDivider />
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterCategoryId('all');
                            setIsCategoryDropdownOpen(false);
                          }}
                        >
                          <ColorDot color="var(--color-border)" />
                          <span>모든 카테고리</span>
                        </FilterOption>
                        {categories.map(category => (
                          <FilterOption 
                            key={category.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterCategoryId(category.id);
                              setIsCategoryDropdownOpen(false);
                            }}
                          >
                            <ColorDot color={category.color} />
                            <span>{category.name}</span>
                          </FilterOption>
                        ))}
                      </FilterDropdown>
                    </>
                  )}
                </FilterSelectContainer>
              )}

              {/* 프로젝트: 프로젝트 드롭다운 (매니저 모드에서는 미표시) */}
              {!forManager && activeTab === 'project' && (
                <FilterSelectContainer>
                  <FilterSelectButton 
                    onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                  >
                    <FilterDropdownTrigger>
                      <ColorDot color={filterProjectId === 'all' ? 'var(--color-border)' : 
                        projects.find(proj => proj.id === filterProjectId)?.color || '#FC5555'} />
                      <span>
                        {filterType === 'all' ? '전체 과제' : filterType === 'incomplete' ? '미완료 과제' : ''}
                        {filterProjectId === 'all' ? (filterType === 'all' || filterType === 'incomplete' ? ' · 모든 프로젝트' : '모든 프로젝트 과제') : 
                         ' · ' + (projects.find(proj => proj.id === filterProjectId)?.title || '프로젝트 선택')}
                      </span>
                    </FilterDropdownTrigger>
                  </FilterSelectButton>
                  
                  {isProjectDropdownOpen && (
                    <>
                      <FilterDropdownOverlay onClick={() => setIsProjectDropdownOpen(false)} />
                      <ProjectFilterDropdown $isOpen={isProjectDropdownOpen}>
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterType('all');
                            setIsProjectDropdownOpen(false);
                          }}
                        >
                          <span>전체 과제</span>
                        </FilterOption>
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterType('incomplete');
                            setIsProjectDropdownOpen(false);
                          }}
                        >
                          <span>미완료 과제</span>
                        </FilterOption>
                        <FilterDivider />
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterProjectId('all');
                            setIsProjectDropdownOpen(false);
                          }}
                        >
                          <ColorDot color="var(--color-border)" />
                          <span>모든 프로젝트</span>
                        </FilterOption>
                        {projects.slice(0, 8).map(project => (
                          <FilterOption 
                            key={project.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterProjectId(project.id);
                              setIsProjectDropdownOpen(false);
                            }}
                          >
                            <ColorDot color={project.color} />
                            <span>{project.title}</span>
                          </FilterOption>
                        ))}
                        {projects.length > 8 && (
                          <div style={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                            borderTop: '1px solid var(--color-border)',
                            paddingTop: '8px'
                          }}>
                            {projects.slice(8).map(project => (
                              <FilterOption
                                key={project.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFilterProjectId(project.id);
                                  setIsProjectDropdownOpen(false);
                                }}
                              >
                                <ColorDot color={project.color} />
                                <span>{project.title}</span>
                              </FilterOption>
                            ))}
                          </div>
                        )}
                      </ProjectFilterDropdown>
                    </>
                  )}
                </FilterSelectContainer>
              )}

              {/* 루틴: 날짜 드롭다운 (매니저/코디 공통) */}
              {(activeTab === 'routine') && (
                <FilterSelectContainer ref={routineDateDropdownRef}>
                  <FilterSelectButton 
                    onClick={() => setIsRoutineDateDropdownOpen(!isRoutineDateDropdownOpen)}
                  >
                    <FilterDropdownTrigger>
                      <ColorDot 
                        color={getRoutineAssignmentColor()} 
                        $isToday={selectedRoutineDate.toDateString() === new Date().toDateString()}
                      />
                      <span>
                        {filterType === 'all' ? '전체 과제' : filterType === 'incomplete' ? '미완료 과제' : ''}
                        {(() => {
                          const today = new Date();
                          const isToday = selectedRoutineDate.toDateString() === today.toDateString();
                          
                          if (filterType === 'all' || filterType === 'incomplete') {
                            if (isToday) {
                              return ' · 오늘';
                            } else {
                              const month = selectedRoutineDate.getMonth() + 1;
                              const day = selectedRoutineDate.getDate();
                              const weekday = selectedRoutineDate.toLocaleDateString('ko-KR', { weekday: 'short' });
                              return ` · ${month}월 ${day}일 (${weekday})`;
                            }
                          } else {
                            if (isToday) {
                              return '오늘';
                            } else {
                              const month = selectedRoutineDate.getMonth() + 1;
                              const day = selectedRoutineDate.getDate();
                              const weekday = selectedRoutineDate.toLocaleDateString('ko-KR', { weekday: 'short' });
                              return `${month}월 ${day}일 (${weekday})`;
                            }
                          }
                        })()}
                      </span>
                    </FilterDropdownTrigger>
                  </FilterSelectButton>
                  
                  {isRoutineDateDropdownOpen && (
                    <>
                      <FilterDropdownOverlay onClick={() => setIsRoutineDateDropdownOpen(false)} />
                      <FilterDropdown $isOpen={isRoutineDateDropdownOpen}>
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterType('all');
                            setIsRoutineDateDropdownOpen(false);
                          }}
                        >
                          <span>전체 과제</span>
                        </FilterOption>
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterType('incomplete');
                            setIsRoutineDateDropdownOpen(false);
                          }}
                        >
                          <span>미완료 과제</span>
                        </FilterOption>
                        <FilterDivider />
                        {getRecentDates().map((dateInfo) => (
                          <FilterOption
                            key={dateInfo.dateId}
                            onClick={() => {
                              setSelectedRoutineDate(dateInfo.date);
                              setIsRoutineDateDropdownOpen(false);
                            }}
                            style={{
                              backgroundColor: selectedRoutineDate.toDateString() === dateInfo.date.toDateString() ? 'var(--color-panel)' : 'var(--color-surface)',
                              fontWeight: dateInfo.isToday ? 'bold' : 'normal'
                            }}
                          >
                            <ColorDot color={getRoutineAssignmentColor()} $isToday={dateInfo.isToday} />
                            <span>
                              {dateInfo.isToday ? '오늘' : `${dateInfo.date.getMonth() + 1}월 ${dateInfo.date.getDate()}일 (${dateInfo.dayOfWeek})`}
                            </span>
                          </FilterOption>
                        ))}
                      </FilterDropdown>
                    </>
                  )}
                </FilterSelectContainer>
              )}

              <ActionButtonsContainer $isAllIncomplete={activeTab === 'allIncomplete'}>
                <SaveButton disabled={!hasChanges || isSaving} onClick={handleSave}>
                  {isSaving ? '저장 중...' : '저장하기'}
                </SaveButton>
              </ActionButtonsContainer>
            </ToggleButtonSection>
          </FilterSection>
        </TopSection>

        {/* 과제 제목 가로 스크롤 영역 */}
        <AssignmentScrollContainer>
          <AssignmentScrollContent>
            {filteredAssignments.map((assignment) => {
              const dueDate = assignment.dueDate || assignment.projectDueDate;
              const dueDateText = dueDate 
                ? `${(dueDate instanceof Date ? dueDate : new Date(dueDate)).getMonth() + 1}/${(dueDate instanceof Date ? dueDate : new Date(dueDate)).getDate()}`
                : '';
              
              // 부여받은 멤버만 카운트하여 과제 완료(확인, 지연확인, 예외) 여부 확인 (assignedStudentIds 기준)
              const assignmentId = getAssignmentId(assignment);
              const isAllCompleted = isAllAssignedCompleted(assignmentId, tableData?.studentStatuses ?? [], assignment);
              
              // 과제 타입에 따른 색상 결정
              let assignmentColor = '#a2a2a2'; // 기본 회색 (테이블과 동일)
              if (assignment.type === 'project') {
                assignmentColor = assignment.projectColor || '#FC5555';  // 프로젝트 색상
              } else if (assignment.type === 'routine') {
                assignmentColor = assignment.color || '#6366f1';  // 루틴 색상
              } else if (assignment.type === 'assignment') {
                // 일반 과제: categoryId로 카테고리 색상 조회
                const category = categories.find(cat => cat.id === assignment.categoryId);
                assignmentColor = category?.color || '#a2a2a2';  // 카테고리 색상 또는 기본 회색 (테이블과 동일)
              }
              
              return (
                <AssignmentBlock
                  key={`${assignment.id}-${assignment.subTaskId || ''}`}
                  $selected={selectedAssignment?.id === assignment.id && 
                    selectedAssignment?.subTaskId === assignment.subTaskId}
                  $completed={isAllCompleted}
                  $color={assignmentColor}
                  onClick={() => handleSelectAssignment(assignment)}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (assignment.type === 'assignment') {
                      handleGeneralAssignmentDoubleClick(assignment.id);
                    } else if (assignment.type === 'project' && assignment.subTaskId) {
                      handleProjectAssignmentDoubleClick(assignment.id, assignment.subTaskId);
                    } else if (assignment.type === 'routine') {
                      handleRoutineAssignmentDoubleClick(assignment.id);
                    }
                  }}
                  data-selected={selectedAssignment?.id === assignment.id && 
                    selectedAssignment?.subTaskId === assignment.subTaskId ? "true" : "false"}
                  data-completed={isAllCompleted ? "true" : "false"}
                >
                  <AssignmentBlockTitle $selected={selectedAssignment?.id === assignment.id && 
                    selectedAssignment?.subTaskId === assignment.subTaskId}>{assignment.title}</AssignmentBlockTitle>
                  {dueDateText && <AssignmentBlockDate $selected={selectedAssignment?.id === assignment.id && 
                    selectedAssignment?.subTaskId === assignment.subTaskId}>{dueDateText}</AssignmentBlockDate>}
                </AssignmentBlock>
              );
            })}
            {filteredAssignments.length === 0 && (
              <EmptyMessage>과제가 없습니다</EmptyMessage>
            )}
          </AssignmentScrollContent>
        </AssignmentScrollContainer>

        {/* 멤버 블럭 그리드 */}
        <>
          {/* [1][2][3][4] 일괄 상태 버튼 (카테고리 탭과 동일 가로 틀, 과제 체크 모달과 동일 디자인) */}
          <BatchStatusToggleSection>
          <BatchStatusButtonsRow>
            {([
              { value: 'pending' as const, label: '미확인', bgColor: 'var(--status-pending)' },
              { value: 'confirmed' as const, label: '확인', bgColor: 'var(--status-confirmed)' },
              { value: 'delayed' as const, label: '지연확인', bgColor: 'var(--status-delayed)' },
              { value: 'exempted' as const, label: '예외', bgColor: 'var(--status-exempted)' }
            ]).map((option, index) => (
              <BatchStatusButton
                key={option.value}
                $disabled={!selectedAssignment || selectedStudents.size === 0}
                onClick={() => !(!selectedAssignment || selectedStudents.size === 0) && handleBatchStatusChange(option.value)}
              >
                <BatchStatusColorDot $bgColor={option.bgColor}>
                  {index + 1}
                </BatchStatusColorDot>
                <BatchStatusText>{option.label}</BatchStatusText>
              </BatchStatusButton>
            ))}
          </BatchStatusButtonsRow>
          </BatchStatusToggleSection>
          <SelectionControlRow>
            <MemberViewModeToggle
              className="touch-compact"
              role="button"
              tabIndex={0}
              $isSeatMode={memberViewMode === 'seat'}
              onClick={() => setMemberViewMode((prev) => (prev === 'number' ? 'seat' : 'number'))}
            >
              <MemberViewModeOption $isActive={memberViewMode === 'number'}>번호</MemberViewModeOption>
              <MemberViewModeOption $isActive={memberViewMode === 'seat'}>자리</MemberViewModeOption>
            </MemberViewModeToggle>

          {/* 전체선택/선택취소 버튼 */}
          <SelectionButtonsRow>
            <SelectionButton 
              onClick={handleSelectAll} 
              tabIndex={-1}
              disabled={!selectedAssignment}
              style={{ opacity: selectedAssignment ? 1 : 0.5 }}
            >
              전체선택
            </SelectionButton>
            <SelectionButton 
              onClick={handleDeselectAll} 
              tabIndex={-1}
              disabled={!selectedAssignment}
              style={{ opacity: selectedAssignment ? 1 : 0.5 }}
            >
              선택취소
            </SelectionButton>
          </SelectionButtonsRow>
          </SelectionControlRow>
          
          {memberViewMode === 'number' ? (
          <TableContainer>
            <StudentsGrid $alignment={gridAlignment}>
              {members.map((member) => {
                const currentStatus = getDisplayStatus(member.id);
                const currentPendingChanges = getCurrentPendingChanges();
                const hasChange = currentPendingChanges.has(member.id);
                const isAssigned = selectedAssignment ? isMemberAssignedToAssignment(member.id, selectedAssignment) : true;
                const isSelected = selectedStudents.has(member.id);
                const isDisabled = !selectedAssignment || !isAssigned;
                const isUnassigned = !!selectedAssignment && !isAssigned;
                
                return (
                  <StudentCard 
                    key={member.id} 
                    $hasChange={hasChange}
                    $isSelected={isSelected}
                    $disabled={isDisabled}
                    $unassigned={isUnassigned}
                  >
                      <StudentCardLayout>
                        <ProfileImageArea $unassigned={isUnassigned} onClick={() => handleStudentSelect(member.id)}>
                          <CachedProfileImage
                            imageId={resolveMemberProfileSource(member, memberProfileDisplayMode)}
                            alt="프로필"
                            fallbackImageId="basic-0"
                          />
                          {isSelected && (
                            <SelectionIndicator>
                              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong)" />
                                <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </SelectionIndicator>
                          )}
                        </ProfileImageArea>
                        
                        <InfoArea>
                          <InfoTopRow $unassigned={isUnassigned}>
                            <StudentNumber>{String(member.number).padStart(2, '0')}</StudentNumber>
                            <StudentName>{member.name}</StudentName>
                          </InfoTopRow>
                          
                          <StatusButtonsRow>
                            {['pending', 'confirmed', 'delayed', 'exempted'].map((status, index) => {
                              const isActive = currentStatus === status;
                              const statusText = status === 'pending' ? '미제출' :
                                                 status === 'confirmed' ? '확인' :
                                                 status === 'delayed' ? '지연' :
                                                 status === 'exempted' ? '예외' : '';
                              return (
                                <StatusButton
                                  key={status}
                                  $status={status}
                                  $isActive={isActive}
                                  $disabled={isDisabled}
                                  onClick={() => !isDisabled && handleStatusChange(member.id, status)}
                                >
                                  {isDisabled ? index + 1 : (isActive ? statusText : index + 1)}
                                </StatusButton>
                              );
                            })}
                          </StatusButtonsRow>
                        </InfoArea>
                      </StudentCardLayout>
                    </StudentCard>
                  );
                })}
              </StudentsGrid>
            </TableContainer>
          ) : (
            <>
            <SeatTableContainer>
              {seatModeData.assigned.length > 0 ? (
                <SeatGridScroll>
                  <SeatStudentsGrid
                    $rows={seatModeData.rowCount}
                    $cols={seatModeData.colCount}
                    $rowTemplate={seatModeData.rowTemplate}
                    $colTemplate={seatModeData.colTemplate}
                  >
                    {seatModeData.assigned.map(({ member, seatRow, seatCol }) => {
                      const currentStatus = getDisplayStatus(member.id);
                      const currentPendingChanges = getCurrentPendingChanges();
                      const hasChange = currentPendingChanges.has(member.id);
                      const isAssigned = selectedAssignment ? isMemberAssignedToAssignment(member.id, selectedAssignment) : true;
                      const isSelected = selectedStudents.has(member.id);
                      const isDisabled = !selectedAssignment || !isAssigned;
                      const isUnassigned = !!selectedAssignment && !isAssigned;
                      const gridRow = seatRow - seatModeData.minRow + 1;
                      const gridCol = seatCol - seatModeData.minCol + 1;

                      return (
                        <SeatCardCell
                          key={member.id}
                          style={{
                            gridRow: `${gridRow} / span 2`,
                            gridColumn: `${gridCol} / span 2`
                          }}
                        >
                          <StudentCard
                            $seat
                            $hasChange={hasChange}
                            $isSelected={isSelected}
                            $disabled={isDisabled}
                            $unassigned={isUnassigned}
                          >
                            <StudentCardLayout $seat>
                              <ProfileImageArea $seat $unassigned={isUnassigned} onClick={() => handleStudentSelect(member.id)}>
                                <CachedProfileImage
                                  imageId={resolveMemberProfileSource(member, memberProfileDisplayMode)}
                                  alt="프로필"
                                  fallbackImageId="basic-0"
                                />
                                {isSelected && (
                                  <SelectionIndicator>
                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong)" />
                                      <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </SelectionIndicator>
                                )}
                              </ProfileImageArea>

                              <InfoArea $seat>
                                <InfoTopRow $unassigned={isUnassigned} $seat>
                                  <StudentNumber>{String(member.number).padStart(2, '0')}</StudentNumber>
                                  <StudentName>{member.name}</StudentName>
                                </InfoTopRow>

                                <StatusButtonsRow $seat>
                                  {['pending', 'confirmed', 'delayed', 'exempted'].map((status, index) => {
                                    const isActive = currentStatus === status;
                                    const statusText = status === 'pending' ? '미제출' :
                                      status === 'confirmed' ? '확인' :
                                        status === 'delayed' ? '지연' :
                                          status === 'exempted' ? '예외' : '';
                                    return (
                                      <StatusButton
                                        key={status}
                                        $status={status}
                                        $isActive={isActive}
                                        $disabled={isDisabled}
                                        onClick={() => !isDisabled && handleStatusChange(member.id, status)}
                                      >
                                        {isDisabled ? index + 1 : (isActive ? statusText : index + 1)}
                                      </StatusButton>
                                    );
                                  })}
                                </StatusButtonsRow>
                              </InfoArea>
                            </StudentCardLayout>
                          </StudentCard>
                        </SeatCardCell>
                      );
                    })}
                  </SeatStudentsGrid>
                </SeatGridScroll>
              ) : (
                <EmptyMessage>자리배치를 먼저 해주세요</EmptyMessage>
              )}
            </SeatTableContainer>

            {/* 포인트 페이지와 동일: 자리 그리드(TableContainer) 밖, 그리드 바로 아래 중앙 */}
            {seatModeData.assigned.length > 0 && (
              <RotateButtonContainer>
                <RotateButton
                  className="touch-compact"
                  type="button"
                  onClick={() => setIsSeatRotated((prev) => !prev)}
                  title="180도 회전"
                >
                  <RotateIconWrapper>
                    <RotateArrowIcon>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 4v16M12 4l-4 4M12 4l4 4M12 20l-4-4M12 20l4-4" />
                      </svg>
                    </RotateArrowIcon>
                    <RotateNumberIcon>180</RotateNumberIcon>
                  </RotateIconWrapper>
                </RotateButton>
              </RotateButtonContainer>
            )}

              {seatModeData.unassigned.length > 0 && (
                <UnassignedSection>
                  <UnassignedTitle>자리 미지정 멤버</UnassignedTitle>
                  <StudentsGrid $alignment={gridAlignment}>
                    {seatModeData.unassigned.map((member) => {
                      const currentStatus = getDisplayStatus(member.id);
                      const currentPendingChanges = getCurrentPendingChanges();
                      const hasChange = currentPendingChanges.has(member.id);
                      const isAssigned = selectedAssignment ? isMemberAssignedToAssignment(member.id, selectedAssignment) : true;
                      const isSelected = selectedStudents.has(member.id);
                      const isDisabled = !selectedAssignment || !isAssigned;
                      const isUnassigned = !!selectedAssignment && !isAssigned;

                      return (
                        <StudentCard
                          $seat
                          key={member.id}
                          $hasChange={hasChange}
                          $isSelected={isSelected}
                          $disabled={isDisabled}
                          $unassigned={isUnassigned}
                        >
                          <StudentCardLayout $seat>
                            <ProfileImageArea $seat $unassigned={isUnassigned} onClick={() => handleStudentSelect(member.id)}>
                              <CachedProfileImage
                                imageId={resolveMemberProfileSource(member, memberProfileDisplayMode)}
                                alt="프로필"
                                fallbackImageId="basic-0"
                              />
                              {isSelected && (
                                <SelectionIndicator>
                                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong)" />
                                    <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </SelectionIndicator>
                              )}
                            </ProfileImageArea>

                            <InfoArea $seat>
                              <InfoTopRow $unassigned={isUnassigned} $seat>
                                <StudentNumber>{String(member.number).padStart(2, '0')}</StudentNumber>
                                <StudentName>{member.name}</StudentName>
                              </InfoTopRow>

                              <StatusButtonsRow $seat>
                                {['pending', 'confirmed', 'delayed', 'exempted'].map((status, index) => {
                                  const isActive = currentStatus === status;
                                  const statusText = status === 'pending' ? '미제출' :
                                    status === 'confirmed' ? '확인' :
                                      status === 'delayed' ? '지연' :
                                        status === 'exempted' ? '예외' : '';
                                  return (
                                    <StatusButton
                                      key={status}
                                      $status={status}
                                      $isActive={isActive}
                                      $disabled={isDisabled}
                                      onClick={() => !isDisabled && handleStatusChange(member.id, status)}
                                    >
                                      {isDisabled ? index + 1 : (isActive ? statusText : index + 1)}
                                    </StatusButton>
                                  );
                                })}
                              </StatusButtonsRow>
                            </InfoArea>
                          </StudentCardLayout>
                        </StudentCard>
                      );
                    })}
                  </StudentsGrid>
                </UnassignedSection>
              )}
            </>
          )}
            
            {memberViewMode === 'number' && (
          <GridAlignmentContainer>
            <GridAlignmentToggle 
              onClick={() => setGridAlignment(prev => prev === 'left' ? 'center' : 'left')}
              tabIndex={-1}
              title={gridAlignment === 'left' ? '중앙 정렬로 변경' : '왼쪽 정렬로 변경'}
            >
              <GridAlignmentOption $isActive={gridAlignment === 'left'}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6h18M3 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </GridAlignmentOption>
              <GridAlignmentOption $isActive={gridAlignment === 'center'}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6h18M6 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </GridAlignmentOption>
            </GridAlignmentToggle>
          </GridAlignmentContainer>
            )}
        </>
      </Content>
      
      {/* 저장 플로팅 버튼 (항상 표시) */}
      <SaveFloatingButton 
        $isDisabled={!hasChanges || isSaving}
        $embeddedInModal={embeddedInModal}
        onClick={handleSave}
        tabIndex={-1}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16L21 8V19C21 20.1046 20.1046 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </SaveFloatingButton>
      
      <ConfirmModal
        isOpen={showWarningModal}
        onClose={handleCloseWarningOnly}
        onCancel={handleExitWithoutSave}
        onConfirm={() => {
          void handleSaveAndExitLocal();
        }}
        title={['변경된 데이터가 있습니다.', '저장하지 않고 닫으시겠습니까?']}
        cancelText="저장하지 않고 닫기"
        confirmText={isSaving ? '저장 중...' : '저장하고 닫기'}
        confirmDisabled={isSaving}
        icon="!"
        iconBackground="var(--status-pending)"
      />

      {/* 일반 과제 체크 모달 */}
      {isGeneralAssignmentCheckModalOpen && selectedGeneralAssignment && (
        <AssignmentCheckModal
          isOpen={isGeneralAssignmentCheckModalOpen}
          onClose={() => {
            setIsGeneralAssignmentCheckModalOpen(false);
            setSelectedGeneralAssignment(null);
            setSelectedGeneralAssignmentStatuses([]);
          }}
          onSave={async (updatedStatuses) => {
            console.log('✅ 일반 과제 상태 업데이트:', updatedStatuses);
            
            try {
              // 서버에 저장
              if (userId && classId && selectedGeneralAssignment?.id) {
                const studentUpdates = updatedStatuses.map(status => ({
                  studentId: status.studentId,
                  status: status.status,
                  earnedPoints: status.earnedPoints || 0,
                  memo: status.memo || '',
                  ...(status.submissionLink !== undefined && { submissionLink: status.submissionLink }),
                  ...(status.submissionMessage !== undefined && { submissionMessage: status.submissionMessage }),
                  ...(status.submittedAt !== undefined && { submittedAt: status.submittedAt })
                }));
                
                await assignmentService.batchUpdateStudentStatusesOptimized(
                  userId,
                  classId,
                  selectedGeneralAssignment.id,
                  studentUpdates
                );
                console.log('✅ 서버에 일반 과제 상태 저장 완료');
              }
              
              // 데이터 새로고침 (열었던 과제 선택 유지)
              await loadData(selectedGeneralAssignment?.id);
              
            } catch (error) {
              console.error('❌ 일반 과제 상태 저장 실패:', error);
              alert('저장 중 오류가 발생했습니다.');
            }
            
            setIsGeneralAssignmentCheckModalOpen(false);
            setSelectedGeneralAssignment(null);
            setSelectedGeneralAssignmentStatuses([]);
          }}
          mode="general"
          memberGroup={currentMemberGroup}
          assignment={selectedGeneralAssignment}
          members={members}
          existingStatuses={selectedGeneralAssignmentStatuses}
          userId={userId}
          classId={classId}
          userType={user?.userType}
        />
      )}

      {/* 프로젝트 과제 체크 모달 */}
      {isProjectAssignmentCheckModalOpen && selectedProjectAssignmentForTable && selectedProjectSubTasksForTable.length > 0 && (
        <AssignmentCheckModal
          isOpen={isProjectAssignmentCheckModalOpen}
          onClose={() => {
            setIsProjectAssignmentCheckModalOpen(false);
            setSelectedProjectAssignmentForTable(null);
            setSelectedProjectSubTasksForTable([]);
            setSelectedSubTaskIdForTable('');
            setSelectedProjectAssignmentStatuses([]);
            setSelectedProjectAssignmentIdForTable('');
          }}
          onSave={async (updatedStatuses) => {
            console.log('✅ 프로젝트 과제 상태 업데이트:', updatedStatuses);
            
            // 모달 상태 초기화 전에 복원용 ID 보관 (일반과제와 동일)
            const restoreId = selectedProjectAssignmentIdForTable || undefined;
            
            try {
              // 서버에 저장
              if (userId && classId && selectedProjectAssignmentForTable && selectedSubTaskIdForTable) {
                await projectAssignmentService.batchUpdateSubTaskStudentStatuses(
                  userId,
                  classId,
                  selectedProjectAssignmentForTable.id,
                  selectedSubTaskIdForTable,
                  updatedStatuses.map(status => {
                    const update: any = {
                      studentId: status.studentId,
                      status: status.status,
                      earnedPoints: status.earnedPoints || 0,
                      memo: status.memo || ''
                    };
                    
                    // 제출 확인 데이터 포함 (있는 경우)
                    if (status.hasOwnProperty('submissionLink')) {
                      update.submissionLink = status.submissionLink;
                    }
                    if (status.hasOwnProperty('submissionMessage')) {
                      update.submissionMessage = status.submissionMessage;
                    }
                    if (status.hasOwnProperty('submittedAt')) {
                      update.submittedAt = status.submittedAt;
                    }
                    
                    return update;
                  }),
                  currentMemberGroup
                );
                console.log('✅ 서버에 프로젝트 과제 상태 저장 완료');
              }
              
              // 일반과제와 동일: 서버에서 새로 로드하고 열었던 과제 선택 유지
              await loadData(restoreId);
              
            } catch (error) {
              console.error('❌ 프로젝트 과제 상태 저장 실패:', error);
              alert('저장 중 오류가 발생했습니다.');
            }
            
            setIsProjectAssignmentCheckModalOpen(false);
            setSelectedProjectAssignmentForTable(null);
            setSelectedProjectSubTasksForTable([]);
            setSelectedSubTaskIdForTable('');
            setSelectedProjectAssignmentStatuses([]);
            setSelectedProjectAssignmentIdForTable('');
          }}
          mode="project"
          memberGroup={currentMemberGroup}
          projectAssignment={selectedProjectAssignmentForTable}
          projectSubTasks={selectedProjectSubTasksForTable}
          selectedSubTaskId={selectedSubTaskIdForTable}
          currentSubTaskIndex={0}
          members={members}
          existingStatuses={selectedProjectAssignmentStatuses}
          userId={userId}
          classId={classId}
          userType={user?.userType}
        />
      )}

      {/* 루틴 과제 체크 모달 */}
      {isRoutineAssignmentCheckModalOpen && selectedRoutineAssignmentForTable && (
        <AssignmentCheckModal
          isOpen={isRoutineAssignmentCheckModalOpen}
          onClose={() => {
            setIsRoutineAssignmentCheckModalOpen(false);
            setSelectedRoutineAssignmentForTable(null);
            setSelectedRoutineDateForTable(new Date());
            setSelectedRoutineAssignmentStatuses([]);
            setSelectedRoutineAssignmentIdForTable('');
          }}
          onSave={async (updatedStatuses) => {
            console.log('✅ 루틴 과제 상태 업데이트:', updatedStatuses);
            
            try {
              // routineId: 통합 서비스 경로용 ID (routine_xxx). assignmentId(routine_xxx-날짜)가 아님.
              const routineId = selectedRoutineAssignmentForTable?.id;
              if (userId && classId && routineId) {
                // 날짜를 YYYY-MM-DD 형식의 ID로 변환
                const dateToDateId = (date: Date): string => {
                  const koreaTime = new Date(date.getTime() + (9 * 60 * 60 * 1000));
                  const dateStr = koreaTime.getUTCFullYear() + '-' + 
                    String(koreaTime.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                    String(koreaTime.getUTCDate()).padStart(2, '0');
                  return dateStr;
                };
                
                const dateId = dateToDateId(selectedRoutineDateForTable);
                await routineAssignmentService.batchUpdateRoutineAssignmentStatusOptimized(
                  userId,
                  classId,
                  routineId,
                  dateId,
                  updatedStatuses.map(s => ({
                    studentId: s.studentId,
                    status: s.status,
                    earnedPoints: s.earnedPoints || 0,
                    memo: s.memo ?? ''
                  })),
                  currentMemberGroup,
                  {
                    fillMissingAssignedAsPending: true,
                    assignedStudentIds: selectedRoutineAssignmentForTable?.assignedStudentIds || []
                  }
                );
                console.log('✅ 서버에 루틴 과제 상태 일괄 저장 완료');
              }
              
              // 데이터 새로고침 (열었던 과제 선택 유지)
              await loadData(selectedRoutineAssignmentIdForTable || undefined);
              
            } catch (error) {
              console.error('❌ 루틴 과제 상태 저장 실패:', error);
              alert('저장 중 오류가 발생했습니다.');
            }
            
            setIsRoutineAssignmentCheckModalOpen(false);
            setSelectedRoutineAssignmentForTable(null);
            setSelectedRoutineDateForTable(new Date());
            setSelectedRoutineAssignmentStatuses([]);
            setSelectedRoutineAssignmentIdForTable('');
          }}
          mode="routine"
          memberGroup={currentMemberGroup}
          routine={selectedRoutineAssignmentForTable}
          members={members}
          existingStatuses={selectedRoutineAssignmentStatuses}
          userId={userId}
          classId={classId}
          userType={user?.userType}
          selectedDate={selectedRoutineDateForTable}
          customDate={selectedRoutineDateForTable}
        />
      )}
    </Container>
  );
};

export default AssignmentCardPage;

// Styled Components - AssignmentTablePageFinal과 동일한 스타일
const Container = styled.div<{ $embeddedInModal?: boolean }>`
  width: 100%;
  min-width: 0;
  min-height: ${props => (props.$embeddedInModal ? '0' : '100vh')};
  ${props =>
    props.$embeddedInModal
      ? `
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
    position: relative;
  `
      : ''}
  background: var(--color-bg);
  padding: ${props => (props.$embeddedInModal ? '0' : '36px 0 32px 0')};
  margin: 0;
`;

const Header = styled.div<{ $embeddedInModal?: boolean }>`
  background: ${props => (props.$embeddedInModal ? 'var(--color-bg)' : 'var(--color-accent-strong)')};
  color: ${props => (props.$embeddedInModal ? 'var(--color-text-primary)' : 'white')};
  padding: ${props => (props.$embeddedInModal ? '16px 20px' : '4px 16px')};
  display: flex;
  align-items: center;
  justify-content: ${props => (props.$embeddedInModal ? 'flex-start' : 'center')};
  position: ${props => (props.$embeddedInModal ? 'sticky' : 'fixed')};
  top: 0;
  left: ${props => (props.$embeddedInModal ? 'auto' : '0')};
  right: ${props => (props.$embeddedInModal ? 'auto' : '0')};
  width: ${props => (props.$embeddedInModal ? '100%' : 'auto')};
  z-index: ${props => (props.$embeddedInModal ? 1100 : 1000)};
  height: ${props => (props.$embeddedInModal ? 'auto' : '36px')};
  min-height: ${props => (props.$embeddedInModal ? '52px' : '36px')};
  border-bottom: ${props => (props.$embeddedInModal ? '1px solid var(--color-border)' : 'none')};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: white;
  width: 28px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ModalCloseButton = styled.button`
  width: 36px;
  height: 36px;
  margin-left: auto;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    color: #6c757d;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Title = styled.h1<{ $embeddedInModal?: boolean }>`
  margin: 0;
  font-size: ${props => (props.$embeddedInModal ? '18px' : '16px')};
  font-weight: ${props => (props.$embeddedInModal ? 700 : 600)};
  color: ${props => (props.$embeddedInModal ? 'var(--color-text-primary)' : 'white')};
  flex: ${props => (props.$embeddedInModal ? '1' : '1')};
  min-width: 0;
  text-align: ${props => (props.$embeddedInModal ? 'left' : 'center')};
`;

const Content = styled.div<{ $embeddedInModal?: boolean }>`
  padding-top: 20px;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 20px;
  ${props =>
    props.$embeddedInModal
      ? `
    height: calc(100% - 52px);
    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: 0;
  `
      : ''}
`;

const TopSection = styled.div`
  position: relative;
  display: block;
  width: 100%;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const ToggleSection = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  max-width: none;
  margin: 0;
  gap: 0;
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  z-index: 1;

  @media (max-width: 900px) {
    position: static;
    left: auto;
    top: auto;
    transform: none;
    width: auto;
    justify-content: center;
  }
`;

/** 일괄 [1][2][3][4] 상태 버튼 행 — 아래 번호/자리·전체선택 줄과 간격 확보 */
const BatchStatusToggleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 600px;
  margin: 0 auto 14px auto;
  gap: 8px;
`;

const ToggleContainer = styled.div<{ $tabCount?: number }>`
  display: flex;
  background: var(--color-border);
  border-radius: 12px;
  padding: 4px;
  position: relative;
  width: 100%;
  max-width: ${props => props.$tabCount === 4 ? '528px' : '384px'};
  min-width: ${props => props.$tabCount === 4 ? '360px' : '240px'};
  min-height: 34px;
  max-height: 34px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    max-width: ${props => props.$tabCount === 4 ? '456px' : '336px'};
    min-width: ${props => props.$tabCount === 4 ? '312px' : '228px'};
  }

  @media (max-width: 480px) {
    max-width: ${props => props.$tabCount === 4 ? '384px' : '264px'};
    min-width: ${props => props.$tabCount === 4 ? '264px' : '192px'};
    min-height: 32px;
    max-height: 32px;
  }
`;

const ToggleSlider = styled.div<{ $position: number; $tabCount?: number }>`
  position: absolute;
  top: 4px;
  left: ${props => {
    const tabCount = props.$tabCount || 3;
    const basePosition = props.$position * (100/tabCount);
    const sliderWidth = 100/tabCount;
    const margin = (100/tabCount - sliderWidth) / 2;
    // 탭별 미세 보정: 첫 탭은 약간 오른쪽, 마지막 탭은 약간 왼쪽
    const adjustment =
      props.$position === 0 ? 0.8 :
      props.$position === tabCount - 1 ? -0.45 :
      0.15;
    return `${basePosition + margin + adjustment}%`;
  }};
  width: ${props => `calc(${100/(props.$tabCount || 3)}% - 4px)`};
  height: calc(100% - 8px);
  background: var(--color-surface);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: left 0.3s ease;
  z-index: 1;
`;

const ToggleOption = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 2;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  color: ${props => props.$active ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.2s ease;
  white-space: nowrap;
  text-align: center;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    padding: 0 8px;
    height: 24px;
    min-height: 24px;
    max-height: 24px;
    font-size: 11px;
  }
`;

const ToggleText = styled.span`
  @media (max-width: 420px) {
    display: none;
  }
`;

const ToggleTextShort = styled.span`
  display: none;
  
  @media (max-width: 420px) {
    display: inline;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  max-width: none;
  margin: 0;
`;


const ToggleButtonSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: none;
  margin: 0;
  gap: 12px;
  flex-wrap: nowrap;
  
  @media (max-width: 480px) {
    gap: 8px;
  }
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const ActionButtonsContainer = styled.div<{ $isAllIncomplete?: boolean }>`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  margin-left: auto;
  
  @media (max-width: 600px) {
    width: 100%;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

// 필터 드롭다운 관련 스타일
const FilterSelectContainer = styled.div`
  position: relative;
  min-width: fit-content;
  width: auto;
`;

const FilterSelectButton = styled.div`
  width: auto;
  min-width: fit-content;
  padding: 4px 0px;
  height: 26px;
  min-height: 26px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--color-text-secondary);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--color-panel);
  }
`;

const FilterDropdownTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  font-size: 14px;
  color: var(--color-text-secondary);
`;

const ColorDot = styled.div<{ color: string; $isToday?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.$isToday ? 'var(--color-border)' : props.color};
`;

const FilterDropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background: transparent;
`;

const FilterDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  min-width: 150px;
  border: 1px solid var(--color-border);
  overflow: hidden;
`;

const ProjectFilterDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  min-width: 200px;
  max-height: 240px;
  border: 1px solid var(--color-border);
  overflow-y: auto;
`;

const FilterOption = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  font-size: 14px;
  color: var(--color-text-secondary);

  &:hover {
    background-color: var(--color-border);
  }
`;

const FilterDropdownOption = FilterOption;

const FilterDivider = styled.div`
  height: 1px;
  background: var(--color-border);
  margin: 4px 0;
`;

const FilterButton = styled.button<{ $active: boolean }>`
  padding: 4px 16px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface);
  color: ${props => props.$active ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: ${props => props.$active ? '600' : '400'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  box-sizing: border-box;
  min-width: 100px;

  &:hover {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(71, 112, 246, 0.3);
  }
  
  @media (max-width: 480px) {
    padding: 4px 8px;
    min-width: 60px;
    font-size: 0.8rem;
  }
`;

const FilterButtonText = styled.span`
  @media (max-width: 480px) {
    display: none;
  }
`;

const FilterButtonTextShort = styled.span`
  display: none;
  
  @media (max-width: 480px) {
    display: inline;
  }
`;

const SaveButton = styled.button<{ disabled: boolean }>`
  padding: 4px 12px;
  height: 26px;
  min-height: 26px;
  width: calc((600px - 24px) / 4);
  max-width: 144px;
  min-width: calc((300px - 24px) / 4);
  background: ${props => props.disabled ? 'var(--color-border)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-dark-gray-text)' : 'white'};
  border: none;
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.6 : 1};
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  
  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
  }
  
  @media (max-width: 600px) {
    width: calc((600px - 24px) / 4);
    max-width: 144px;
    min-width: calc((300px - 24px) / 4);
  }
`;

// 과제 스크롤 영역
const AssignmentScrollContainer = styled.div`
  background: var(--color-surface);
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
  overflow-x: auto;
  width: 100%;
  min-height: 68px;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--color-panel);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--color-border-strong);
    border-radius: 3px;
  }
`;

const AssignmentScrollContent = styled.div`
  display: flex;
  gap: 8px;
  min-width: min-content;
  justify-content: center;
`;

const AssignmentBlock = styled.button<{ $selected: boolean; $completed?: boolean; $color?: string }>`
  padding: 6px 16px;
  padding-top: 10px;
  border: ${props => 
    props.$selected ? '2px solid var(--color-text-muted)' : 
    props.$completed ? '1px solid var(--color-border)' : '1px solid var(--color-border)'};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background-color: ${props => props.$color || 'var(--color-border)'};
  }
  background: ${props => {
    // 선택된 블럭도 배경색은 그대로 유지
    if (props.$completed) {
      return 'var(--color-surface-highlight)';
    }
    // 선택되지 않은 블럭: 투명 배경
    return 'transparent';
  }};
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  box-shadow: ${props => 
    props.$completed ? 'none' :
    props.$selected ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none'};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  opacity: ${props => {
    if (props.$completed && !props.$selected) {
      return 0.7;
    }
    // 선택되지 않은 블럭은 CSS 변수를 통해 opacity 관리 (다크 모드에서 0.6, 일반 모드에서 1)
    if (!props.$selected && !props.$completed) {
      // CSS 변수를 직접 사용할 수 없으므로, 전역 CSS에서 처리
      return 1;
    }
    return 1;
  }};
  
  &:hover {
    border-color: ${props => 
      props.$selected ? 'var(--color-text-muted)' :
      props.$completed ? '#ccc' : 'var(--color-text-muted)'};
    background: ${props => {
      // 완료된 블럭은 호버 시에도 원래 배경색(회색) 유지
      if (props.$completed) {
        return 'var(--color-surface-highlight)';
      }
      // 선택된 블럭은 호버 시에도 원래 배경색(transparent) 유지
      if (props.$selected) {
        return 'transparent';
      }
      return 'transparent';
    }};
    box-shadow: ${props => 
      props.$selected ? '0 4px 12px rgba(0, 0, 0, 0.18)' :
      props.$completed ? 'none' : '0 4px 8px rgba(0, 0, 0, 0.12)'};
  }
  
  &:active {
    background: ${props => {
      // 완료된 블럭은 active 시에도 원래 배경색(회색) 유지
      if (props.$completed) {
        return 'var(--color-surface-highlight)';
      }
      // 선택된 블럭은 active 시에도 원래 배경색(transparent) 유지
      if (props.$selected) {
        return 'transparent';
      }
      return 'transparent';
    }};
    transform: scale(0.98);
  }
`;

const AssignmentBlockTitle = styled.span<{ $selected?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
`;

const AssignmentBlockDate = styled.span<{ $selected?: boolean }>`
  font-size: 10px;
  color: var(--color-text-muted);
`;

const EmptyMessage = styled.span`
  color: var(--color-text-muted);
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  width: 100%;
`;

// 일괄 상태 버튼 행 (ToggleSection 안에서 사용, 가로 틀은 ToggleSection과 동일)
const BatchStatusButtonsRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  justify-content: center;
  height: 32px;
  min-height: 32px;
  align-items: center;

  @media (max-width: 420px) {
    height: 28px;
    min-height: 28px;
    gap: 4px;
  }
  @media (max-width: 480px) {
    gap: 6px;
    flex-wrap: nowrap;
    height: 28px;
    min-height: 28px;
  }
  @media (max-width: 380px) {
    gap: 2px;
    flex-wrap: nowrap;
  }
  @media (max-width: 350px) {
    gap: 1px;
    flex-wrap: nowrap;
  }
`;

const BatchStatusButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 6px 12px;
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  transition: all 0.2s ease;
  flex: 1;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  opacity: ${props => props.$disabled ? 0.6 : 1};

  &:hover {
    background-color: ${props => props.$disabled ? 'var(--color-surface)' : 'var(--color-panel)'};
    border-color: ${props => props.$disabled ? 'var(--color-border)' : 'var(--color-text-muted)'};
    box-shadow: ${props => props.$disabled ? '0 2px 4px rgba(0, 0, 0, 0.1)' : '0 4px 8px rgba(0, 0, 0, 0.15)'};
  }
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  @media (max-width: 420px) {
    justify-content: center;
    padding: 4px;
    height: 28px;
    min-width: 24px;
    flex-shrink: 1;
  }
  @media (max-width: 480px) {
    padding: 4px 8px;
    height: 28px;
  }
  @media (max-width: 380px) {
    padding: 2px 4px;
    height: 28px;
    min-width: 20px;
  }
  @media (max-width: 350px) {
    padding: 1px 2px;
    height: 28px;
    min-width: 18px;
  }
`;

const BatchStatusColorDot = styled.div<{ $bgColor: string }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${props => props.$bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-weight: 700;
  font-size: 12px;
  color: white;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  flex-shrink: 0;

  @media (max-width: 420px) {
    margin-right: 0;
    width: 20px;
    height: 20px;
    font-size: 12px;
  }
`;

const BatchStatusText = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text-primary);
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 600px) {
    font-size: 12px;
  }
  @media (max-width: 480px) {
    font-size: 11px;
  }
  @media (max-width: 420px) {
    display: none;
  }
`;

// 선택 버튼 영역
const SelectionControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

const SelectionButtonsRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
`;

const MemberViewModeToggle = styled.div<{ $isSeatMode: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  background: var(--color-accent-strong);
  border-radius: 13px;
  padding: 2px;
  height: 26px;
  min-width: 120px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$isSeatMode ? '50%' : '2px')};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: var(--color-surface);
    border-radius: 11px;
    transition: left 0.2s ease;
    z-index: 1;
  }
`;

const MemberViewModeOption = styled.div<{ $isActive: boolean }>`
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  color: ${(p) => (p.$isActive ? 'var(--color-toggle-active)' : 'var(--color-toggle-inactive)')};
  white-space: nowrap;
`;

const SelectionButton = styled.button`
  padding: 4px 8px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: var(--color-panel);
    border-color: rgba(var(--color-accent-rgb), 0.5);
  }
  
  &:active {
    background: var(--color-border);
  }
`;

// 테이블 컨테이너
const TableContainer = styled.div`
  background: transparent;
  border: 2px dashed var(--color-border);
  border-radius: 8px;
  overflow: hidden;
  overflow-x: auto;
  box-shadow: none;
`;

const SeatTableContainer = styled.div`
  background: transparent;
  border: 2px dashed var(--color-border);
  border-radius: 8px;
  overflow: hidden;
  overflow-x: auto;
  box-shadow: none;
`;

const SeatGridScroll = styled.div`
  width: 100%;
  overflow: auto;
  padding: 12px;
`;

const SeatStudentsGrid = styled.div<{ $rows: number; $cols: number; $rowTemplate: string; $colTemplate: string }>`
  display: grid;
  grid-template-columns: ${(p) => p.$colTemplate};
  grid-template-rows: ${(p) => p.$rowTemplate};
  gap: 4px;
  width: 100%;
  min-width: max-content;
  justify-content: center;
`;

const SeatCardCell = styled.div`
  min-width: 144px;
  min-height: 84px;
`;

/* PointsTablePage 자리 모드와 동일한 180° 회전 버튼 */
const RotateButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 8px;
`;

const RotateButton = styled.button`
  width: 60px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  min-width: 60px;
  box-sizing: border-box;
  line-height: 1;
  background: var(--color-grid-alignment-container);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &:hover {
    border-color: var(--color-border);
    background: var(--color-grid-alignment-container);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:active {
    transform: none;
  }
`;

const RotateIconWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: var(--color-grid-alignment-active-bg);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
`;

const RotateArrowIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transform: scale(1);
  transition: opacity 0.3s ease, transform 0.3s ease;

  svg {
    width: 22px;
    height: 22px;
  }

  :root[data-theme='light'] & svg {
    stroke: #717785;
  }

  ${RotateButton}:hover & {
    opacity: 0;
    transform: scale(0.8);
  }
`;

const RotateNumberIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: currentColor;
  opacity: 0;
  transform: scale(0.8);
  transition: opacity 0.3s ease, transform 0.3s ease;

  :root[data-theme='light'] & {
    color: #717785;
  }

  ${RotateButton}:hover & {
    opacity: 1;
    transform: scale(1);
  }
`;

const UnassignedSection = styled.div`
  margin-top: 16px;
  border-top: 1px solid var(--color-border);
  padding-top: 12px;
`;

const UnassignedTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 8px;
`;

// 멤버 그리드 - PointsTablePage와 동일
const StudentsGrid = styled.div<{ $alignment?: 'left' | 'center' }>`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;
  justify-content: ${props => props.$alignment === 'center' ? 'center' : 'flex-start'};
  
  & > * {
    flex: 0 0 auto;
    width: calc((100% - 64px) / 5);
    min-width: 180px;
    max-width: calc((100% - 64px) / 5);
  }
  
  @media (max-width: 1400px) {
    & > * {
      width: calc((100% - 48px) / 4);
      max-width: calc((100% - 48px) / 4);
    }
  }
  
  @media (max-width: 1100px) {
    & > * {
      width: calc((100% - 32px) / 3);
      max-width: calc((100% - 32px) / 3);
    }
  }
  
  @media (max-width: 800px) {
    & > * {
      width: calc((100% - 16px) / 2);
      max-width: calc((100% - 16px) / 2);
    }
  }
  
  @media (max-width: 500px) {
    & > * {
      width: 100%;
      max-width: none;
    }
  }
`;

// 그리드 정렬 컨테이너
const GridAlignmentContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px;
`;

// 그리드 정렬 토글 버튼
const GridAlignmentToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 2px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  min-width: 60px;
  box-sizing: border-box;
  line-height: 1;
  background: var(--color-grid-alignment-container);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  
  &:hover {
    background: var(--color-grid-alignment-container);
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 그리드 정렬 옵션
const GridAlignmentOption = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 20px;
  border-radius: 4px;
  background: ${props => props.$isActive ? 'var(--color-grid-alignment-active-bg)' : 'transparent'};
  color: ${props => props.$isActive ? 'var(--color-grid-alignment-active-icon)' : 'var(--color-grid-alignment-icon)'};
  box-shadow: ${props => props.$isActive ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'};
  transition: all 0.2s ease;
`;

const StudentCard = styled.div<{ $hasChange?: boolean; $isSelected?: boolean; $disabled?: boolean; $unassigned?: boolean; $seat?: boolean }>`
  /* 미부여: 활성 블럭과 동일(배경·테두리·opacity·filter 없음). 과제 미선택 시에만 비활성 스타일 */
  background: ${props => {
    if (props.$disabled && !props.$unassigned) return 'rgba(255, 255, 255, 0.04)';
    if (props.$hasChange) return 'rgba(var(--color-accent-rgb), 0.08)';
    return 'var(--color-surface)';
  }};
  :root[data-theme='light'] & {
    background: ${props => {
      if (props.$disabled && !props.$unassigned) return 'rgba(255, 255, 255, 0.04)';
      if (props.$hasChange) return 'rgba(var(--color-accent-rgb), 0.08)';
      return 'rgba(255, 255, 255, 0.6)';
    }};
  }
  :root:not([data-theme='light']) & {
    background: ${props => {
      if (props.$disabled && !props.$unassigned) return 'rgba(255, 255, 255, 0.04)';
      if (props.$hasChange) return 'rgba(255, 255, 255, 0.06)';
      return 'var(--color-surface)';
    }};
  }
  border: 2px solid ${props => {
    if (props.$disabled && !props.$unassigned) return 'var(--color-border)';
    if (props.$isSelected) return 'rgba(var(--color-accent-rgb), 0.5)';
    return 'var(--color-border)';
  }};
  :root:not([data-theme='light']) & {
    border-color: ${props => {
      if (props.$disabled && !props.$unassigned) return 'var(--color-border)';
      if (props.$isSelected) return 'rgba(255, 255, 255, 0.6)';
      return 'var(--color-border)';
    }};
  }
  border-radius: ${props => props.$seat ? '10px' : '8px'};
  padding: ${props => props.$seat ? '4px 4px 6px 4px' : '8px'};
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  transition: all 0.2s;
  height: ${props => props.$seat ? '100%' : '120px'};
  min-width: 0;
  overflow: hidden;
  opacity: ${props => (props.$disabled && !props.$unassigned) ? 0.6 : 1};
  filter: ${props => (props.$disabled && !props.$unassigned) ? 'grayscale(100%)' : 'none'};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};

  &:hover {
    transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$disabled ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.15)'};
    border-color: ${props => (props.$disabled && !props.$unassigned) ? 'var(--color-border)' : 'rgba(var(--color-accent-rgb), 0.5)'};
  }
  :root:not([data-theme='light']) &:hover {
    border-color: ${props => (props.$disabled && !props.$unassigned) ? 'var(--color-border)' : 'rgba(255, 255, 255, 0.6)'};
    box-shadow: ${props => props.$disabled ? 'none' : '0 4px 12px rgba(255, 255, 255, 0.15)'};
  }
`;

const StudentCardLayout = styled.div<{ $seat?: boolean }>`
  display: grid;
  /* 번호 모드: auto+width:100% 조합이 모바일 WebKit에서 1열을 과대 확장시키는 경우가 있어 프로필 열 폭 고정 */
  grid-template-columns: ${props => (props.$seat ? 'auto 1fr' : 'minmax(0, 104px) 1fr')};
  gap: ${props => props.$seat ? '4px' : '8px'};
  height: 100%;
  min-height: 0;
  min-width: 0;
`;

const ProfileImageArea = styled.div<{ $unassigned?: boolean; $seat?: boolean }>`
  width: ${props => props.$seat ? '80px' : '100%'};
  min-width: ${props => props.$seat ? '80px' : '0'};
  max-width: ${props => props.$seat ? '80px' : '100%'};
  height: ${props => props.$seat ? '80px' : '100%'};
  min-height: ${props => props.$seat ? '80px' : '0'};
  max-height: ${props => props.$seat ? '80px' : '100%'};
  background: transparent;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  grid-row: 1 / 2;
  grid-column: 1 / 2;
  aspect-ratio: 1;
  max-height: 100%;
  align-self: center;
  justify-self: stretch;
  cursor: pointer;
  transition: opacity 0.2s;
  border: 2px solid #000;
  border-radius: 10px;
  filter: ${props => props.$unassigned ? 'grayscale(100%)' : 'none'};
  opacity: ${props => props.$unassigned ? 0.5 : 1};

  img {
    border-radius: 0;
    object-fit: cover;
    width: 100%;
    height: 100%;
    display: block;
    max-width: 100%;
    -webkit-touch-callout: none;
  }
  
  &:hover {
    transform: scale(1.05);
  }
`;

const SelectionIndicator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const InfoArea = styled.div<{ $seat?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  grid-row: 1 / 2;
  grid-column: 2 / 3;
  justify-content: ${props => props.$seat ? 'flex-start' : 'space-between'};
`;

const InfoTopRow = styled.div<{ $unassigned?: boolean; $seat?: boolean }>`
  display: flex;
  gap: 4px;
  align-items: baseline;
  margin-bottom: ${props => props.$seat ? '1px' : '4px'};
  opacity: ${props => props.$unassigned ? 0.5 : 1};
`;

const StudentNumber = styled.span`
  font-size: 16px;
  color: #666;
  font-weight: 500;
  flex-shrink: 0;
  
  /* 다크모드: 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    color: #777777;
  }
`;

const StudentName = styled.span`
  color: var(--color-text-primary);
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

// 상태 버튼 2x2 그리드
const StatusButtonsRow = styled.div<{ $seat?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: ${props => props.$seat ? '26px 26px' : '32px 32px'};
  gap: ${props => props.$seat ? '3px' : '4px'};
  width: 100%;
  height: ${props => props.$seat ? '56px' : '68px'};
  margin-top: ${props => props.$seat ? '0' : '0'};
  flex-shrink: 0;

  @media (max-width: 500px) {
    ${props =>
      !props.$seat &&
      `
      grid-template-rows: 28px 28px;
      height: 60px;
      gap: 3px;
    `}
  }
`;

const StatusButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })<{ $status: string; $isActive: boolean; $disabled?: boolean }>`
  width: 100%;
  height: 100%;
  min-height: 0;
  max-height: none;
  border: none;
  background: ${props => 
    props.$disabled ? '#E0E0E0' :
    props.$isActive ? statusColors[props.$status]?.bg || 'var(--color-border)' : 'var(--color-border)'};
  color: ${props => 
    props.$disabled ? 'var(--color-text-muted)' :
    props.$isActive ? statusColors[props.$status]?.text || 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: none;
  /* 비활성(클릭 전) 회색: 라이트는 #E0E0E0, 50% 불투명 */
  :root[data-theme='light'] & {
    background: ${props => 
      props.$disabled ? '#E0E0E0' :
      props.$isActive ? statusColors[props.$status]?.bg || 'var(--color-border)' : 'var(--color-block-border)'};
  }
  /* 비활성(1,2,3,4) 버튼: 다크모드에서 #373737, 50% 불투명도 */
  :root:not([data-theme='light']) &,
  [data-theme='dark'] & {
    background: ${props => 
      props.$disabled ? '#373737' :
      props.$isActive ? statusColors[props.$status]?.bg || 'var(--color-border)' : 'var(--color-block-border)'};
  }
  
  &:hover {
    opacity: ${props => props.$disabled ? 0.5 : 0.9};
  }
  
  &:active {
    transform: ${props => props.$disabled ? 'none' : 'translateY(1px)'};
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  @media (max-width: 500px) {
    font-size: 12px;
  }
`;

// 빈 상태
const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 300px;
  padding: 40px;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
`;

const EmptyStateText = styled.p`
  font-size: 16px;
  color: var(--color-text-muted);
`;

// 로딩
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 300px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border);
  border-top-color: var(--color-accent-strong);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: 14px;
  color: #666;
  
  /* 다크모드: 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    color: #777777;
  }
`;

// 저장 플로팅 버튼 (항상 표시)
const SaveFloatingButton = styled.button<{ $isDisabled?: boolean; $embeddedInModal?: boolean }>`
  position: ${props => (props.$embeddedInModal ? 'absolute' : 'fixed')};
  bottom: ${props =>
    props.$embeddedInModal
      ? '16px'
      : 'calc(env(safe-area-inset-bottom, 0px) + 68px)'};
  right: ${props => (props.$embeddedInModal ? '16px' : '42px')};
  margin-left: 0;
  margin-right: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$isDisabled ? '#ccc' : '#10504A'};
  color: white;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  box-shadow: ${props => props.$isDisabled ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 4px 16px rgba(var(--color-accent-rgb), 0.3)'};
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$isDisabled ? 0.6 : 1};
  
  &:hover {
    background: ${props => props.$isDisabled ? '#ccc' : '#0D4039'};
    transform: ${props => props.$isDisabled ? 'none' : 'scale(1.05)'};
    box-shadow: ${props => props.$isDisabled ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 6px 20px rgba(var(--color-accent-rgb), 0.4)'};
  }
  
  &:active {
    transform: ${props => props.$isDisabled ? 'none' : 'scale(0.95)'};
  }
  
  /* 다크모드: 흰색 버튼에 진한 회색 아이콘 */
  :root:not([data-theme='light']) & {
    background: ${props => props.$isDisabled ? '#666' : '#ffffff'};
    border: 1px solid var(--color-border);
    color: ${props => props.$isDisabled ? '#999' : '#505050'};
    box-shadow: ${props => props.$isDisabled ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 4px 16px rgba(0, 0, 0, 0.2)'};
  }
  
  :root:not([data-theme='light']) &:hover {
    background: ${props => props.$isDisabled ? '#666' : '#ffffff'};
    border-color: var(--color-text-muted);
    color: ${props => props.$isDisabled ? '#999' : '#505050'};
    box-shadow: ${props => props.$isDisabled ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 6px 20px rgba(0, 0, 0, 0.3)'};
  }
  
  :root:not([data-theme='light']) & svg {
    stroke: ${props => props.$isDisabled ? '#999' : '#505050'};
  }
`;
